// src/pages/appointments/DoctorsPage.jsx
import React from "react";
import Card from "@/components/ui/Card.jsx";
import { useNavigate, useLocation } from "react-router-dom";

import DoctorMap from "@/components/maps/DoctorMap.jsx";
import DoctorResultCard from "@/components/doctor/DoctorResultCard.jsx";
import { getAfriMedMapStyle } from "@/constants/mapStyles.js";
import { useAppointmentsFilters } from "@/pages/appointments/AppointmentsFiltersContext.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import SearchBar from "@/sot/primitives/SearchBar.jsx";
import useGeolocation from "@/hooks/useGeolocation.js";
import LocationConsentDialog from "@/components/location/LocationConsentDialog.jsx";

import {
  fetchDoctorsDirectoryBrowse,
  isReadyForPatientDiscovery,
  resolveDoctorAvatarSrc,
} from "@/lib/doctorsDirectory.js";
import { readDoctorConsultancies } from "@/lib/localDoctorStorage";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import {
  getDoctorBookingAvailability,
} from "@/services/doctorAvailabilityService.js";
import {
  buildFavoriteDoctorIds,
  isFavoriteDoctor,
  subscribeDoctorFavorites,
} from "@/services/doctorFavorites.js";
import supabase from "@/services/supabaseClient";
import { AFRIMED_MARKETS } from "@/config/markets.js";
import { formatPatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { recordStagingPerformance } from "@/lib/stagingPerformanceTrace.js";

/**
 * DEMO DECORATORS
 * Used ONLY when DB fields are missing.
 */
const DEMO_DECORATORS = {
  "5155ca42-1871-4cf5-974a-04c7942ecb12": {
    avatar: "/images/demo/doctors/avatar-01.png",
    address: "Seogoditshane Way, 5th floor, 507",
    languages: "English, Tswana",
    serviceLabel: "Virtual consultation",
    serviceLabelPrice: "150.00",
  },
  "f2ec893d-3762-4a42-a44c-99659b694edb": {
    avatar: "/images/demo/doctors/avatar-03.png",
    address: "Sidilega, Gaborone",
    languages: "English, Tswana",
    serviceLabel: "First time consultancy",
    serviceLabelPrice: "100.00",
  },

  // ✅ IMPORTANT: keep DB-like ids EXACT (this must match row.doctor_user_id)
  "30264165-45b1-474f-b0df-f15359229718": {
    avatar: "/images/demo/doctors/dr-hendrik.jpg",
    address: "Netcare, Gaborone",
    languages: "English, Tswana",
    serviceLabel: "First time consultancy",
    serviceLabelPrice: "100.00",
  },

  // ✅ padding-only demo ids (invented + guaranteed unique)
  "demo-pad-1": {
    avatar: "/images/demo/doctors/dr-dineo.jpg",
    address: "Gaborone",
    languages: "English, Tswana",
    serviceLabel: "First time consultancy",
    serviceLabelPrice: "100.00",
  },
  "demo-pad-2": {
    avatar: "/images/demo/doctors/dr_2126.jpg",
    address: "Gaborone",
    languages: "English, Tswana",
    serviceLabel: "First time consultancy",
    serviceLabelPrice: "100.00",
  },
  "demo-pad-3": {
    avatar: "/images/demo/doctors/dr-neo-moloi.jpg",
    address: "Gaborone",
    languages: "English, Tswana",
    serviceLabel: "Virtual consultation",
    serviceLabelPrice: "150.00",
  },
};

const NEUTRAL_AFRICA_CENTER = { lat: -8.7832, lng: 25.5085, zoom: 3.4 };

function normalizeSearchTargetKey(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const COUNTRY_SEARCH_ALIASES = {
  cd: "DR Congo",
  cod: "DR Congo",
  congo: "DR Congo",
  "congo kinshasa": "DR Congo",
  "congo drc": "DR Congo",
  "democratic republic of congo": "DR Congo",
  "democratic republic of the congo": "DR Congo",
  drc: "DR Congo",
  "dr congo": "DR Congo",
  "d r congo": "DR Congo",
  "d r c": "DR Congo",
  britain: "United Kingdom",
  england: "United Kingdom",
  gb: "United Kingdom",
  "great britain": "United Kingdom",
  greatbritain: "United Kingdom",
  kinshasa: "DR Congo",
  madagascar: "Madagascar",
  "federal republic of nigeria": "Nigeria",
  "republic of kenya": "Kenya",
  "republic of madagascar": "Madagascar",
  "republic of rwanda": "Rwanda",
  rsa: "South Africa",
  "south africa": "South Africa",
  tanzania: "Tanzania",
  uk: "United Kingdom",
  "united arab emirates": "United Arab Emirates",
  "united kingdom": "United Kingdom",
  "united republic of tanzania": "Tanzania",
  usa: "United States",
  "united states": "United States",
  "united states of america": "United States",
};

const COUNTRY_ALIASES = COUNTRY_SEARCH_ALIASES;

const COUNTRY_MAP_METADATA = {
  Angola: { lat: -11.2027, lng: 17.8739, zoom: 5.1 },
  Australia: { lat: -25.2744, lng: 133.7751, zoom: 3.7 },
  Austria: { lat: 47.5162, lng: 14.5501, zoom: 6.0 },
  Belgium: { lat: 50.5039, lng: 4.4699, zoom: 6.2 },
  Botswana: { lat: -22.3285, lng: 24.6849, zoom: 5.4 },
  Cameroon: { lat: 7.3697, lng: 12.3547, zoom: 5.2 },
  Canada: { lat: 56.1304, lng: -106.3468, zoom: 3.2 },
  "DR Congo": { lat: -2.8799, lng: 23.656, zoom: 4.4 },
  Eswatini: { lat: -26.5225, lng: 31.4659, zoom: 7.2 },
  France: { lat: 46.2276, lng: 2.2137, zoom: 5.4 },
  Germany: { lat: 51.1657, lng: 10.4515, zoom: 5.5 },
  Ghana: { lat: 7.9465, lng: -1.0232, zoom: 5.4 },
  Kenya: { lat: 0.0236, lng: 37.9062, zoom: 5.4 },
  Lesotho: { lat: -29.61, lng: 28.2336, zoom: 7.0 },
  Madagascar: { lat: -18.7669, lng: 46.8691, zoom: 5.2 },
  Malawi: { lat: -13.2543, lng: 34.3015, zoom: 6.0 },
  Mauritius: { lat: -20.3484, lng: 57.5522, zoom: 8.2 },
  Mozambique: { lat: -18.6657, lng: 35.5296, zoom: 5.0 },
  Namibia: { lat: -22.9576, lng: 18.4904, zoom: 5.1 },
  Nigeria: { lat: 9.082, lng: 8.6753, zoom: 5.0 },
  "New Zealand": { lat: -40.9006, lng: 174.886, zoom: 5.0 },
  Rwanda: { lat: -1.9403, lng: 29.8739, zoom: 7.0 },
  Seychelles: { lat: -4.6796, lng: 55.492, zoom: 7.2 },
  "South Africa": { lat: -30.5595, lng: 22.9375, zoom: 4.8 },
  Switzerland: { lat: 46.8182, lng: 8.2275, zoom: 6.2 },
  Tanzania: { lat: -6.369, lng: 34.8888, zoom: 5.2 },
  "United Arab Emirates": { lat: 23.4241, lng: 53.8478, zoom: 6.0 },
  "United Kingdom": { lat: 54.7024, lng: -3.2766, zoom: 5.2 },
  "United States": { lat: 39.8283, lng: -98.5795, zoom: 3.5 },
  Zambia: { lat: -13.1339, lng: 27.8493, zoom: 5.3 },
  Zimbabwe: { lat: -19.0154, lng: 29.1549, zoom: 5.7 },
};

const CITY_SEARCH_TARGETS = {
  accra: { label: "Accra, Ghana", country: "Ghana", city: "Accra", lat: 5.6037, lng: -0.187, zoom: 9.0 },
  antananarivo: { label: "Antananarivo, Madagascar", country: "Madagascar", city: "Antananarivo", lat: -18.8792, lng: 47.5079, zoom: 9.0 },
  abuja: { label: "Abuja, Nigeria", country: "Nigeria", city: "Abuja", lat: 9.0765, lng: 7.3986, zoom: 9.0 },
  "cape town": { label: "Cape Town, South Africa", country: "South Africa", city: "Cape Town", lat: -33.9249, lng: 18.4241, zoom: 9.0 },
  "dar es salaam": { label: "Dar es Salaam, Tanzania", country: "Tanzania", city: "Dar es Salaam", lat: -6.7924, lng: 39.2083, zoom: 9.0 },
  gaborone: { label: "Gaborone, Botswana", country: "Botswana", city: "Gaborone", lat: -24.6282, lng: 25.9231, zoom: 9.0 },
  harare: { label: "Harare, Zimbabwe", country: "Zimbabwe", city: "Harare", lat: -17.8252, lng: 31.0335, zoom: 9.0 },
  kinshasa: { label: "Kinshasa, Democratic Republic of Congo", country: "DR Congo", city: "Kinshasa", lat: -4.4419, lng: 15.2663, zoom: 8.6 },
  kigali: { label: "Kigali, Rwanda", country: "Rwanda", city: "Kigali", lat: -1.9441, lng: 30.0619, zoom: 9.0 },
  lagos: { label: "Lagos, Nigeria", country: "Nigeria", city: "Lagos", lat: 6.5244, lng: 3.3792, zoom: 9.0 },
  london: { label: "London, United Kingdom", country: "United Kingdom", city: "London", lat: 51.5072, lng: -0.1276, zoom: 9.2 },
  lusaka: { label: "Lusaka, Zambia", country: "Zambia", city: "Lusaka", lat: -15.3875, lng: 28.3228, zoom: 9.0 },
  nairobi: { label: "Nairobi, Kenya", country: "Kenya", city: "Nairobi", lat: -1.2921, lng: 36.8219, zoom: 9.0 },
  yaounde: { label: "Yaoundé, Cameroon", country: "Cameroon", city: "Yaoundé", lat: 3.848, lng: 11.5021, zoom: 9.0 },
  yaoundé: { label: "Yaoundé, Cameroon", country: "Cameroon", city: "Yaoundé", lat: 3.848, lng: 11.5021, zoom: 9.0 },
};

function buildCountryTarget(country, label = country) {
  const normalizedCountry = COUNTRY_SEARCH_ALIASES[normalizeSearchTargetKey(country)] || country;
  const metadata = COUNTRY_MAP_METADATA[normalizedCountry] || COUNTRY_MAP_METADATA[country];
  if (!metadata) return null;
  return {
    label: label || normalizedCountry,
    country: normalizedCountry,
    lat: metadata.lat,
    lng: metadata.lng,
    zoom: metadata.zoom || 5.2,
  };
}

function buildMarketSearchTargets() {
  const targets = { ...CITY_SEARCH_TARGETS };

  for (const market of AFRIMED_MARKETS) {
    const country = normalizeSearchTargetKey(market?.name);
    const target = buildCountryTarget(market?.name, market?.name);
    if (!country || !target) continue;

    targets[country] = target;

    const iso = normalizeSearchTargetKey(market?.iso);
    if (iso) targets[iso] = target;

    const dial = normalizeSearchTargetKey(market?.dialCode);
    if (dial) targets[dial] = target;
  }

  for (const [alias, country] of Object.entries(COUNTRY_SEARCH_ALIASES)) {
    const target = buildCountryTarget(country, alias === "england" ? "England, United Kingdom" : country);
    if (target) targets[normalizeSearchTargetKey(alias)] = target;
  }

  return targets;
}

const PLACE_SEARCH_TARGETS = buildMarketSearchTargets();

const DIAL_CODE_COUNTRIES = [
  ["+243", "DR Congo"],
  ["+260", "Zambia"],
  ["+263", "Zimbabwe"],
  ["+237", "Cameroon"],
  ["+233", "Ghana"],
  ["+254", "Kenya"],
  ["+234", "Nigeria"],
  ["+250", "Rwanda"],
  ["+255", "Tanzania"],
  ["+267", "Botswana"],
  ["+27", "South Africa"],
];

const UNAMBIGUOUS_CURRENCY_COUNTRIES = {
  KES: "Kenya",
  NGN: "Nigeria",
  RWF: "Rwanda",
  ZMW: "Zambia",
};

// Icons (your paths)
const ICONS = {
  back: "/icons/filter-doctors/back-arrow.svg",
  cancel: "/icons/doctor/cancel.svg",
  railPrev: "/icons/doctor/appointments/arrow_back.svg",
  railNext: "/icons/doctor/appointments/arrow_next.svg",

  verified: "/icons/patient/verified_filled.svg",
  nearby: "/icons/filter-doctors/distance.svg",
  language: "/icons/filter-doctors/language.svg",
  evisit: "/icons/filter-doctors/video_call.svg",
  filter: "/icons/filter-doctors/date_range_filled-dark.svg",

  specialty: "/icons/services/doctor.svg",
  date: "/icons/filter-doctors/date_range_filled-dark.svg",
};

function compactPlaceKey(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, "")
    .trim();
}

const MARKET_PLACE_ALIASES = AFRIMED_MARKETS.reduce((acc, market) => {
  const name = String(market?.name || "").trim().toLowerCase();
  const iso = compactPlaceKey(market?.iso);
  const dial = compactPlaceKey(market?.dialCode);
  if (iso && name) acc[iso] = name;
  if (dial && name) acc[dial] = name;
  return acc;
}, {});

const PLACE_ALIASES = {
  ...MARKET_PLACE_ALIASES,
  capetown: "cape town",
  "cape-town": "cape town",
  gb: "united kingdom",
  "great britain": "united kingdom",
  greatbritain: "united kingdom",
  joburg: "johannesburg",
  jozi: "johannesburg",
  uk: "united kingdom",
};

const MARKET_COUNTRY_NAMES = AFRIMED_MARKETS
  .map((market) => String(market?.name || "").trim())
  .filter(Boolean);

const FRENCH_DIRECTORY_LABELS = {
  specialty: {
    Doctor: "Médecin",
    "General Practice": "Médecine générale",
    "general practitioner": "Médecin généraliste",
    Dermatologist: "Dermatologue",
    Dermatology: "Dermatologie",
    "Preventive Medicine": "Médecine préventive",
    "Emergency Medicine": "Médecine d’urgence",
    "Allergy/Immunology": "Allergologie / Immunologie",
    "Allergy and Immunology": "Allergologie / Immunologie",
    Pediatric: "Pédiatrie",
    Paediatric: "Pédiatrie",
  },
  language: {
    English: "Anglais",
    French: "Français",
    Tswana: "Setswana",
    Setswana: "Setswana",
    "Zimbabwean Ndebele": "Ndebele du Zimbabwe",
    Ndebele: "Ndebele",
    Portuguese: "Portugais",
    German: "Allemand",
  },
  service: {
    "Virtual consultation": "Consultation virtuelle",
    "General consultation": "Consultation générale",
    "First time consultancy": "Première consultation",
    "Professional mole assessment": "Évaluation professionnelle des grains de beauté",
    "Allergy testing and management": "Tests et prise en charge des allergies",
    "Food Allergy Management": "Prise en charge des allergies alimentaires",
    "Asthma and Respiratory Care": "Prise en charge de l’asthme et des troubles respiratoires",
    "Atopic Dermatitis (Eczema)": "Dermatite atopique (eczéma)",
    "Primary Immune Deficiency Diseases (PIDDs)": "Déficits immunitaires primitifs",
    Consultation: "Consultation",
    "Additional service": "Service complémentaire",
  },
};

function isFrenchLanguageCode(languageCode = "") {
  return String(languageCode || "").trim().toLowerCase().startsWith("fr");
}

function localizeDirectoryLabel(value, languageCode, group) {
  const raw = String(value || "").trim();
  if (!raw || !isFrenchLanguageCode(languageCode)) return raw;
  return FRENCH_DIRECTORY_LABELS[group]?.[raw] || raw;
}

function localizeDirectoryList(value, languageCode, group) {
  const raw = String(value || "").trim();
  if (!raw || !isFrenchLanguageCode(languageCode)) return raw;
  return raw
    .split(",")
    .map((item) => localizeDirectoryLabel(item.trim(), languageCode, group))
    .join(", ");
}

function localizeDoctorCard(card, languageCode) {
  if (!card || !isFrenchLanguageCode(languageCode)) return card;
  return {
    ...card,
    specialty: localizeDirectoryLabel(card.specialty, languageCode, "specialty"),
    serviceLabel: localizeDirectoryLabel(card.serviceLabel, languageCode, "service"),
    serviceName: localizeDirectoryLabel(card.serviceName, languageCode, "service"),
    languages: localizeDirectoryList(card.languages, languageCode, "language"),
  };
}

function titleCase(s = "") {
  return String(s)
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatDoctorCardName(rawName = "") {
  return formatPatientFacingDoctorName(rawName);
}

function hasDisplayText(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  return text !== "—" && text !== "-";
}

function isGenericDoctorText(value) {
  const text = String(value || "").trim().toLowerCase();
  return text === "doctor" || text === "dr" || text === "dr.";
}

function firstDisplayText(...values) {
  for (const value of values) {
    if (!hasDisplayText(value)) continue;
    return String(value).trim();
  }
  return "";
}

function normalizeCountryText(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = raw
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (COUNTRY_ALIASES[key]) return COUNTRY_ALIASES[key];
  const marketMatch = MARKET_COUNTRY_NAMES.find((name) => {
    const normalizedName = name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return key === normalizedName || key.includes(normalizedName);
  });
  return marketMatch || raw;
}

function payoutProfileAddress(row = {}) {
  const payoutMeta = row?.payout_meta && typeof row.payout_meta === "object" ? row.payout_meta : {};
  return payoutMeta.profile_address && typeof payoutMeta.profile_address === "object"
    ? payoutMeta.profile_address
    : {};
}

function resolveCardOperatingCountry(row = {}) {
  return resolveCardCountryInference(row).country;
}

function countryCentroid(country) {
  const normalized = normalizeCountryText(country);
  const metadata = COUNTRY_MAP_METADATA[normalized] || COUNTRY_MAP_METADATA[country] || null;
  if (!metadata) return null;
  return { lat: metadata.lat, lng: metadata.lng };
}

function resolvePatientProfileMapCenter(profile = {}) {
  const address = profile?.profile_address && typeof profile.profile_address === "object"
    ? profile.profile_address
    : {};
  const country = firstDisplayText(
    profile?.country,
    profile?.profile_country,
    profile?.address_country,
    profile?.country_code,
    profile?.phone_country,
    address?.country,
    address?.country_code
  );
  const coords = countryCentroid(country);
  return coords ? { ...coords, zoom: 5.2 } : null;
}

function resolveCountrySearchFocus(value = "") {
  const normalized = normalizePlace(value);
  if (!normalized) return null;
  return PLACE_SEARCH_TARGETS[normalized] || null;
}

function resolveAvatarSrc(row, demoAvatar = "") {
  return resolveDoctorAvatarSrc(row, demoAvatar || "/images/demo/doctors/avatar-fallback.jpg");
}

function parseFeesObject(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  const raw = String(value || "").trim();
  if (!raw || !raw.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function pickFeeFromFeesObject(fees) {
  if (!fees || typeof fees !== "object") return null;
  const candidates = [
    fees.consultation_fee,
    fees.consultation_price,
    fees.consultationPrice,
    fees.default_fee,
    fees.default_price,
    fees.evisit_price,
    fees.e_visit_price,
    fees.in_person_price,
    fees.price,
  ];
  for (const value of candidates) {
    const n = toNum(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function pickCurrencyFromFeesObject(fees) {
  if (!fees || typeof fees !== "object") return "";
  const candidates = [
    fees.currency,
    fees.consultation_currency,
    fees.default_currency,
    fees.evisit_currency,
    fees.e_visit_currency,
    fees.in_person_currency,
  ];
  for (const value of candidates) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function resolveCountryFromPhone(value) {
  const digits = String(value || "").replace(/[^\d+]/g, "");
  if (!digits) return "";
  const normalized = digits.startsWith("+") ? digits : `+${digits}`;
  const match = DIAL_CODE_COUNTRIES.find(([dialCode]) => normalized.startsWith(dialCode));
  return match?.[1] || "";
}

function resolveCountryFromCurrency(...values) {
  for (const value of values) {
    const currency = String(value || "").trim().toUpperCase();
    if (!currency) continue;
    if (UNAMBIGUOUS_CURRENCY_COUNTRIES[currency]) {
      return UNAMBIGUOUS_CURRENCY_COUNTRIES[currency];
    }
  }
  return "";
}

function resolveCardCountryInference(row = {}) {
  const payoutMeta = row?.payout_meta && typeof row.payout_meta === "object" ? row.payout_meta : {};
  const profileAddress = payoutProfileAddress(row);
  const fees = parseFeesObject(row?.consultation_fees);
  const explicitCandidates = [
    [row?.country_inference_source || "operating_country", row?.operating_country],
    ["practice_country", row?.practice_country],
    ["primary_practice_country", row?.primary_practice_country],
    ["facility_country", row?.facility_country],
    ["profile_country", row?.country],
    ["payment_setup_country", payoutMeta.payment_setup_country],
    ["profile_address_country", profileAddress.country],
    ["profile_address_country_code", profileAddress.country_code],
    ["register_country", row?.register_country],
    ["registration_country", row?.registration_country],
    ["market_country", row?.market_country],
  ];
  for (const [source, value] of explicitCandidates) {
    const country = normalizeCountryText(value);
    if (country) return { country, source };
  }

  const phoneCountry = resolveCountryFromPhone(
    firstDisplayText(row?.phone, row?.phone_number, row?.mobile_phone, row?.contact_phone)
  );
  if (phoneCountry) return { country: phoneCountry, source: "phone_dial_code" };

  const currencyCountry = resolveCountryFromCurrency(
    row?.service_currency,
    row?.currency,
    pickCurrencyFromFeesObject(fees)
  );
  if (currencyCountry) return { country: currencyCountry, source: "pricing_currency" };

  return { country: "", source: "" };
}

function isVirtualReadyDoctor(row = {}) {
  const practiceMode = String(row?.practice_mode || row?.practiceMode || "")
    .trim()
    .toLowerCase();
  const evisitStatus = String(row?.evisit_status || row?.eVisitStatus || "")
    .trim()
    .toLowerCase();

  return (
    practiceMode === "virtual_only" ||
    practiceMode === "virtual" ||
    (practiceMode === "hybrid" &&
      (row?.evisit_enabled === true ||
        row?.telehealth_ready === true ||
        row?.accepts_evisit === true ||
        row?.accepts_virtual === true ||
        !!row?.evisit_availability ||
        !!row?.telehealth_availability)) ||
    row?.evisit_enabled === true ||
    row?.telehealth_ready === true ||
    row?.accepts_evisit === true ||
    row?.accepts_virtual === true ||
    evisitStatus === "on" ||
    evisitStatus === "enabled" ||
    !!row?.evisit_availability ||
    !!row?.telehealth_availability
  );
}

function pickRatingValue(row = {}) {
  const candidates = [
    row?.rating,
    row?.avg_rating,
    row?.average_rating,
    row?.rating_avg,
    row?.review_rating,
    row?.score,
  ];
  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

// Demo switches (padding OFF, decorators ON)
const ENABLE_DEMO_PADDING = false;
const ENABLE_DEMO_DECORATORS = true;

function toCardModel(row) {
  // Demo decorators only (no padding rows)
  const isDemoRow =
    !!row?.is_demo || String(row?.doctor_user_id || "").startsWith("demo-pad");
  const demo =
    isDemoRow &&
    ENABLE_DEMO_DECORATORS &&
    typeof DEMO_DECORATORS === "object" &&
    DEMO_DECORATORS
      ? DEMO_DECORATORS[row?.doctor_user_id] || {}
      : {};

  const yearsRaw = Number(
    row?.years_of_experience ?? row?.years_experience ?? row?.yearsExperience
  );
  const ratingRaw = pickRatingValue(row);
  const feesObject = parseFeesObject(row?.consultation_fees);
  const countryInference = resolveCardCountryInference(row);
  const operatingCountry = countryInference.country;
  const facilityName = firstDisplayText(
    row?.facility_name ||
      row?.display_location ||
      operatingCountry ||
      row?.facility_city ||
      row?.hospital ||
      row?.institution_slug ||
      "",
  );
  const facilityAddressParts = [
    String(row?.facility_address || row?.street_address || row?.address || "").trim(),
    String(row?.facility_city || "").trim(),
    String(row?.facility_country || operatingCountry || "").trim(),
  ].filter(Boolean);
  const facilityAddress = titleCase(facilityAddressParts.join(", "));
  const facilityCityCountry = titleCase(
    [row?.facility_city, row?.facility_country]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(", ")
  );
  const displayLocation = firstDisplayText(
    row?.display_location,
    row?.facility_city,
    operatingCountry,
    row?.facility_country,
    row?.facility_name,
    row?.city,
    row?.country,
  );
  const rawLat = toNum(
    row?.facility_lat ??
      row?.facility_latitude ??
      row?.lat ??
      row?.latitude
  );
  const rawLng = toNum(
    row?.facility_lng ??
      row?.facility_longitude ??
      row?.lng ??
      row?.lon ??
      row?.longitude
  );
  const preciseCoords = normalizeFacilityCoords(rawLat, rawLng);
  const fallbackCountryCoords = countryCentroid(operatingCountry);
  const hasPreciseCoords = isValidLatLng(preciseCoords.lat, preciseCoords.lng);
  const lat = hasPreciseCoords ? preciseCoords.lat : fallbackCountryCoords?.lat ?? null;
  const lng = hasPreciseCoords ? preciseCoords.lng : fallbackCountryCoords?.lng ?? null;
  const mapLocationPrecision = hasPreciseCoords
    ? "facility"
    : fallbackCountryCoords
      ? "country"
      : "unavailable";
  const specialtyFromArray = firstTextFromUnknown(
    row?.specialties,
    row?.specialization,
    row?.speciality
  );
  const languagesJoined = listTextFromUnknown(
    row?.languages_spoken ?? row?.languages ?? row?.language
  ).join(", ");

  const derivedConsultationFee = resolveMeaningfulPrice(
    row?.consultation_fee,
    pickFeeFromFeesObject(feesObject)
  );
  const primaryPrice = formatPrice(
    resolveMeaningfulPrice(row?.service_price, derivedConsultationFee)
  );
  const secondaryPrice = formatPrice(resolveMeaningfulPrice(row?.secondary_service_price));
  const primaryLabel = String(row?.service_label || "").trim();
  const secondaryLabel = String(row?.secondary_service_label || "").trim();
  const specialtyText =
    [
      String(row?.specialty || "").trim(),
      String(row?.speciality || "").trim(),
      String(row?.primary_specialty || "").trim(),
      String(specialtyFromArray || "").trim(),
    ].find((value) => hasDisplayText(value) && !isGenericDoctorText(value)) ||
    "Doctor";
  const derivedCurrency =
    String(
      row?.currency ||
        row?.service_currency ||
        pickCurrencyFromFeesObject(feesObject) ||
        "",
    ).trim() || (isDemoRow ? "BWP" : "ZAR");

  return {
    id: row?.doctor_user_id,
    doctor_user_id: row?.doctor_user_id || null,
    user_id: row?.doctor_user_id || row?.user_id || null,
    profile_id: row?.id || null,
    name: formatDoctorCardName(row?.display_name || row?.full_name || "Doctor"),
    specialty: specialtyText,
    yearsExperience:
      Number.isFinite(yearsRaw) && yearsRaw > 0 ? Math.round(yearsRaw) : "—",
    rating: Number.isFinite(ratingRaw) ? ratingRaw.toFixed(1) : "—",
    verified: row?.verified_status === "verified",
    verificationStatus:
      String(row?.verified_status || "").toLowerCase().trim() === "verified"
        ? "verified"
        : "pending",
    practice_mode: row?.practice_mode || null,
    practiceMode: row?.practice_mode || null,

    hospital: titleCase(facilityName || displayLocation),
    address:
      facilityCityCountry ||
      facilityAddress ||
      titleCase(displayLocation) ||
      titleCase(demo.address || ""),
    mapAddress:
      facilityAddress ||
      facilityCityCountry ||
      titleCase(displayLocation) ||
      titleCase(demo.address || ""),
    mapLocationPrecision,
    operatingCountry,
    countrySource: countryInference.source,

    serviceLabel:
      primaryLabel || (primaryPrice ? "Consultation" : "") || demo.serviceLabel || "",
    serviceLabelPrice: primaryPrice || demo.serviceLabelPrice || "",

    serviceName: secondaryLabel || (secondaryPrice ? "Additional service" : ""),
    serviceNamePrice: secondaryPrice,

    currency: derivedCurrency,
    languages: languagesJoined || demo.languages || "",
    displayLocation: titleCase(displayLocation),
    insurancePolicy: String(row?.insurance_policy || row?.insurancePolicy || "").trim(),
    insurance_policy: String(row?.insurance_policy || row?.insurancePolicy || "").trim(),

    isFavorite: false,
    avatar: resolveAvatarSrc(row, demo?.avatar || ""),
    lat,
    lng,
    facility_id: row?.consultancy_id || null,
    facility: {
      id: row?.consultancy_id || null,
      name: titleCase(facilityName),
      address: facilityAddress,
      city: titleCase(row?.facility_city || ""),
      country: titleCase(row?.facility_country || operatingCountry || ""),
      insurance_policy: String(row?.insurance_policy || row?.insurancePolicy || "").trim(),
      lat,
      lng,
    },
  };
}

function makeDemoRow(doctor_user_id) {
  // Only used if ENABLE_DEMO_DOCTORS is turned on
  const short = String(doctor_user_id || "").slice(0, 4).toUpperCase();

  return {
    doctor_user_id,
    display_name: `Demo Doctor ${short}`, // ✅ no more Lucy x3
    specialty: "General Practice",
    years_of_experience: null,
    verified_status: "verified",
    facility_name: "Life Gaborone Private Hospital",
    facility_address: "Gaborone",
    languages_spoken: ["English", "Tswana"],
    service_label: "First time consultancy",
    service_price: "100.00",
    secondary_service_label: "",
    secondary_service_price: "",
    profile_photo_path: null,
  };
}

// ✅ random pad ids (unique, reshuffled each load)
function pickRandomPadIds(need) {
  const pool = ["demo-pad-1", "demo-pad-2", "demo-pad-3"];
  // Fisher–Yates shuffle
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  // If need > pool size (unlikely), cycle
  const out = [];
  for (let i = 0; i < need; i++) {
    out.push(pool[i % pool.length]);
  }
  return out;
}

/** small geo helpers for facilities */
function toNum(v) {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : null;
}

function inSadcBounds(lat, lng) {
  const la = Number(lat);
  const lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return false;
  return la >= -40 && la <= 8 && lo >= 8 && lo <= 45;
}

function normalizeFacilityCoords(latValue, lngValue) {
  const lat = toNum(latValue);
  const lng = toNum(lngValue);
  if (!isValidLatLng(lat, lng)) return { lat: null, lng: null };
  if (inSadcBounds(lat, lng)) return { lat, lng };

  // Legacy rows occasionally have lat/lng swapped.
  if (isValidLatLng(lng, lat) && inSadcBounds(lng, lat)) {
    return { lat: lng, lng: lat };
  }
  return { lat, lng };
}

function listTextFromUnknown(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v || "").trim()).filter(Boolean);
  }
  const raw = String(value || "").trim();
  if (!raw) return [];
  if (raw.startsWith("[") && raw.endsWith("]")) {
    try {
      return listTextFromUnknown(JSON.parse(raw));
    } catch {
      // continue with string parsing
    }
  }
  if (raw.includes(",")) {
    return raw
      .split(",")
      .map((v) => String(v || "").trim())
      .filter(Boolean);
  }
  return [raw];
}

function firstTextFromUnknown(...values) {
  for (const value of values) {
    const list = listTextFromUnknown(value);
    if (list.length > 0) return list[0];
    const raw = String(value || "").trim();
    if (raw) return raw;
  }
  return "";
}

function formatPrice(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value).trim();
  if (n <= 0) return "";
  return n.toFixed(2);
}

function resolveMeaningfulPrice(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) {
      if (n > 0) return n;
      continue;
    }
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}

function isValidLatLng(lat, lng) {
  const la = Number(lat);
  const lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return false;
  if (la < -90 || la > 90 || lo < -180 || lo > 180) return false;
  if (Math.abs(la) < 0.000001 && Math.abs(lo) < 0.000001) return false;
  return true;
}

function isPreferredMapLatLng(lat, lng) {
  return isValidLatLng(lat, lng) && inSadcBounds(lat, lng);
}

function isDoctorMapTargetLatLng(lat, lng) {
  return isValidLatLng(lat, lng);
}

function haversineKm(a, b) {
  const lat1 = Number(a?.lat);
  const lng1 = Number(a?.lng);
  const lat2 = Number(b?.lat);
  const lng2 = Number(b?.lng);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return Number.POSITIVE_INFINITY;

  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const la1 = (lat1 * Math.PI) / 180;
  const la2 = (lat2 * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

function normalizePlace(value) {
  const normalized = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return PLACE_ALIASES[normalized] || normalized;
}

function compactPlace(value) {
  return normalizePlace(value).replace(/\s+/g, "");
}

function containsNormalizedLocation(base = "", location = "") {
  const normalizedBase = normalizePlace(base);
  const normalizedLocation = normalizePlace(location);
  if (!normalizedBase || !normalizedLocation) return false;
  return (
    normalizedBase === normalizedLocation ||
    normalizedBase.includes(normalizedLocation) ||
    compactPlace(normalizedBase).includes(compactPlace(normalizedLocation))
  );
}

function formatFacilityLocation(value = {}) {
  const address = String(
    value?.address || value?.facility_address || value?.street_address || ""
  ).trim();
  const city = String(value?.city || value?.facility_city || "").trim();
  const country = String(value?.country || value?.facility_country || "").trim();
  const locationTail = [city, country].filter(Boolean).join(", ");
  if (!address) return locationTail;
  if (!locationTail) return address;
  if (containsNormalizedLocation(address, locationTail)) return address;
  return `${address}, ${locationTail}`;
}

function matchesCountrySearchFocus(row = {}, countryFocus = null) {
  if (!countryFocus?.country) return false;
  const operatingCountry = resolveCardOperatingCountry(row);
  const targetCountry = normalizeCountryText(countryFocus.country);
  if (targetCountry && normalizeCountryText(operatingCountry) === targetCountry) return true;

  const normalizedCountry = normalizePlace(targetCountry);
  const normalizedCity = normalizePlace(countryFocus.city || "");
  const normalizedLabel = normalizePlace(countryFocus.label || "");
  const compactCountry = compactPlace(targetCountry);
  const compactCity = compactPlace(countryFocus.city || "");
  const compactLabel = compactPlace(countryFocus.label || "");
  const haystack = [
    row?.operating_country,
    operatingCountry,
    row?.practice_country,
    row?.primary_practice_country,
    row?.facility_country,
    row?.country,
    row?.display_location,
    row?.facility_city,
    row?.facility_address,
    row?.street_address,
    row?.address,
    row?.hospital,
    row?.facility_name,
  ]
    .map((value) => normalizePlace(value))
    .filter(Boolean)
    .join(" ");
  const compactHaystack = compactPlace(haystack);

  return (
    (!!normalizedCity && haystack.includes(normalizedCity)) ||
    (!!compactCity && compactHaystack.includes(compactCity)) ||
    (!!normalizedCountry && haystack.includes(normalizedCountry)) ||
    (!!compactCountry && compactHaystack.includes(compactCountry)) ||
    (!!normalizedLabel && haystack.includes(normalizedLabel)) ||
    (!!compactLabel && compactHaystack.includes(compactLabel))
  );
}

function matchesDirectorySearch(row = {}, search = "") {
  const normalizedSearch = normalizePlace(search);
  if (!normalizedSearch) return true;
  const countryFocus = resolveCountrySearchFocus(search);
  if (countryFocus?.country) {
    return matchesCountrySearchFocus(row, countryFocus);
  }

  const operatingCountry = resolveCardOperatingCountry(row);
  const compactSearch = compactPlace(search);
  const haystack = [
    row?.display_name,
    row?.full_name,
    row?.first_name,
    row?.last_name,
    row?.specialty,
    row?.speciality,
    row?.primary_specialty,
    ...listTextFromUnknown(row?.specialties),
    ...listTextFromUnknown(row?.specialization),
    ...listTextFromUnknown(row?.languages_spoken),
    ...listTextFromUnknown(row?.languages),
    row?.facility_name,
    row?.operating_country,
    operatingCountry,
    row?.display_location,
    row?.facility_city,
    row?.facility_country,
    row?.facility_address,
    row?.street_address,
    row?.address,
  ]
    .map((value) => normalizePlace(value))
    .filter(Boolean)
    .join(" ");

  const compactHaystack = haystack.replace(/\s+/g, "");
  return haystack.includes(normalizedSearch) || compactHaystack.includes(compactSearch);
}

function mergeLoadedDoctorRows(loadedRows = [], sourceRows = []) {
  const byId = new Map();

  for (const row of Array.isArray(sourceRows) ? sourceRows : []) {
    const id = String(row?.doctor_user_id || row?.user_id || "").trim();
    if (!id) continue;
    byId.set(id, row);
  }

  for (const row of Array.isArray(loadedRows) ? loadedRows : []) {
    const id = String(row?.doctor_user_id || row?.user_id || "").trim();
    if (!id) continue;
    const prev = byId.get(id) || {};
    byId.set(id, {
      ...prev,
      ...row,
      doctor_user_id: id,
      full_name: String(row?.full_name || "").trim() || prev?.full_name || null,
      display_name: String(row?.display_name || "").trim() || prev?.display_name || null,
      specialty: String(row?.specialty || "").trim() || prev?.specialty || null,
      primary_specialty:
        String(row?.primary_specialty || "").trim() || prev?.primary_specialty || null,
      facility_name: String(row?.facility_name || "").trim() || prev?.facility_name || null,
      facility_address:
        String(row?.facility_address || "").trim() || prev?.facility_address || null,
      facility_city: String(row?.facility_city || "").trim() || prev?.facility_city || null,
      facility_country:
        String(row?.facility_country || "").trim() || prev?.facility_country || null,
      operating_country:
        String(row?.operating_country || "").trim() ||
        resolveCardOperatingCountry(row) ||
        prev?.operating_country ||
        resolveCardOperatingCountry(prev) ||
        null,
      country_inference_source:
        resolveCardCountryInference(row).source ||
        row?.country_inference_source ||
        prev?.country_inference_source ||
        resolveCardCountryInference(prev).source ||
        null,
      payout_meta: row?.payout_meta ?? prev?.payout_meta ?? null,
      register_country: row?.register_country ?? prev?.register_country ?? null,
      phone: row?.phone ?? prev?.phone ?? null,
      display_location:
        String(row?.display_location || "").trim() || prev?.display_location || null,
      consultation_fee: row?.consultation_fee ?? prev?.consultation_fee ?? null,
      consultation_fees: row?.consultation_fees ?? prev?.consultation_fees ?? null,
      service_label: String(row?.service_label || "").trim() || prev?.service_label || "",
      service_price: row?.service_price ?? prev?.service_price ?? null,
      service_currency:
        String(row?.service_currency || row?.currency || "").trim() ||
        prev?.service_currency ||
        prev?.currency ||
        "",
      currency:
        String(row?.currency || row?.service_currency || "").trim() ||
        prev?.currency ||
        prev?.service_currency ||
        "",
      languages_spoken:
        Array.isArray(row?.languages_spoken) && row.languages_spoken.length > 0
          ? row.languages_spoken
          : prev?.languages_spoken || null,
      consultancy_id: row?.consultancy_id || prev?.consultancy_id || null,
      insurance_policy:
        String(row?.insurance_policy || row?.insurancePolicy || "").trim() ||
        prev?.insurance_policy ||
        prev?.insurancePolicy ||
        null,
      practice_mode:
        String(row?.practice_mode || row?.practiceMode || "").trim() ||
        prev?.practice_mode ||
        prev?.practiceMode ||
        null,
    });
  }

  return Array.from(byId.values());
}

function scoreFacilityAreaMatch(card, focus) {
  if (!card || !focus) return 0;

  const focusCity = normalizePlace(focus?.city);
  const focusCountry = normalizePlace(focus?.country);
  const focusAddress = normalizePlace(focus?.address);
  const candidateCity = normalizePlace(card?.facility?.city || card?.displayLocation || "");
  const candidateCountry = normalizePlace(card?.facility?.country || "");
  const candidateTexts = [
    normalizePlace(card?.hospital),
    normalizePlace(card?.address),
    normalizePlace(card?.mapAddress),
    candidateCity,
    candidateCountry,
  ].filter(Boolean);

  let score = 0;

  if (focusCountry && candidateCountry && focusCountry === candidateCountry) {
    score += 2;
  }

  if (
    focusCity &&
    candidateTexts.some(
      (text) => text === focusCity || text.includes(focusCity) || focusCity.includes(text)
    )
  ) {
    score += 6;
  }

  if (
    focusAddress &&
    candidateTexts.some(
      (text) => text === focusAddress || text.includes(focusAddress) || focusAddress.includes(text)
    )
  ) {
    score += 3;
  }

  return score;
}

function scoreFacilityFocusMatch(focus, candidate = {}) {
  return scoreFacilityAreaMatch(
    {
      hospital: candidate?.name || "",
      address: candidate?.address || "",
      mapAddress: candidate?.address || "",
      displayLocation: candidate?.city || "",
      facility: {
        city: candidate?.city || "",
        country: candidate?.country || "",
      },
    },
    focus
  );
}

function matchesIncomingFacility(card, focus) {
  if (!card || !focus) return true;

  const focusId = String(focus?.id || "").trim();
  if (focusId) {
    const candidateIds = [
      String(card?.facility_id || "").trim(),
      String(card?.facility?.id || "").trim(),
    ].filter(Boolean);
    if (candidateIds.includes(focusId)) return true;
  }

  const focusTexts = [
    normalizePlace(focus?.name),
    normalizePlace(focus?.address),
  ].filter(Boolean);
  if (focusTexts.length === 0) return true;

  const candidateTexts = [
    normalizePlace(card?.hospital),
    normalizePlace(card?.facility?.name),
    normalizePlace(card?.address),
    normalizePlace(card?.mapAddress),
    normalizePlace(card?.displayLocation),
    normalizePlace(card?.facility?.address),
  ].filter(Boolean);

  return focusTexts.some((focusText) =>
    candidateTexts.some(
      (candidateText) =>
        candidateText === focusText ||
        candidateText.includes(focusText) ||
        focusText.includes(candidateText)
    )
  );
}

// ✅ tiny helper: toggles a string in an array (SSOT style)
function toggleInArray(arr, value) {
  const v = String(value);
  const list = Array.isArray(arr) ? [...arr] : [];
  const i = list.indexOf(v);
  if (i >= 0) list.splice(i, 1);
  else list.push(v);
  return list;
}

function categoryToSearch(category = "") {
  const key = String(category || "").toLowerCase().trim();
  if (key === "hospitals") return "hospital";
  if (key === "ambulances") return "ambulance";
  if (key === "pharmacies") return "pharmacy";
  return "";
}

const DIRECTORY_CONTEXT = {
  doctors: {
    key: "doctors",
    singular: "Clinician",
    plural: "Clinicians",
    searchSeed: "Clinician",
    searchPlaceholder: "Dermatologist",
  },
  hospitals: {
    key: "hospitals",
    singular: "Hospital",
    plural: "Hospitals",
    searchSeed: "Hospital",
    searchPlaceholder: "Hospital",
  },
  pharmacies: {
    key: "pharmacies",
    singular: "Pharmacy",
    plural: "Pharmacies",
    searchSeed: "Pharmacy",
    searchPlaceholder: "Pharmacy",
  },
  ambulances: {
    key: "ambulances",
    singular: "Ambulance",
    plural: "Ambulances",
    searchSeed: "Ambulance",
    searchPlaceholder: "Ambulance",
  },
};

function directoryKeyFromSearchValue(value = "") {
  const key = String(value || "").toLowerCase().trim();
  if (key === "hospital" || key === "hospitals") return "hospitals";
  if (key === "pharmacy" || key === "pharmacies") return "pharmacies";
  if (key === "ambulance" || key === "ambulances") return "ambulances";
  return "doctors";
}

function resolveDirectoryContext(pathname = "", search = "") {
  const path = String(pathname || "").toLowerCase().trim();
  if (path.startsWith("/appointments/hospitals")) return DIRECTORY_CONTEXT.hospitals;
  if (path.startsWith("/appointments/pharmacies")) return DIRECTORY_CONTEXT.pharmacies;
  if (path.startsWith("/ambulances")) return DIRECTORY_CONTEXT.ambulances;

  try {
    const params = new URLSearchParams(search || "");
    const categorySearch = categoryToSearch(params.get("category"));
    if (categorySearch) {
      return DIRECTORY_CONTEXT[directoryKeyFromSearchValue(categorySearch)] || DIRECTORY_CONTEXT.doctors;
    }

    const q = String(params.get("q") || "").trim();
    if (q) {
      return DIRECTORY_CONTEXT[directoryKeyFromSearchValue(q)] || DIRECTORY_CONTEXT.doctors;
    }
  } catch {
    // ignore malformed search params
  }

  return DIRECTORY_CONTEXT.doctors;
}

export default function DoctorsPage() {
  const nav = useNavigate();
  const { user, profile, loading, BYPASS } = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const { isDark } = useTheme();
  const { filters, setFilters, openSheet } = useAppointmentsFilters();
  const geo = useGeolocation();
  const mapboxToken = String(import.meta.env.VITE_MAPBOX_TOKEN || "").trim();
  const geocodeCacheRef = React.useRef(new Map());
  const desktopRailRef = React.useRef(null);
  const routeStartedAtRef = React.useRef(
    typeof performance !== "undefined" ? performance.now() : 0
  );
  const firstCardPaintedRef = React.useRef(false);

  // ---- state (declare BEFORE anything that uses it) ----
  const [rows, setRows] = React.useState([]);
  const [loadingList, setLoadingList] = React.useState(true);
  const [error, setError] = React.useState("");
  const [, setFavoriteVersion] = React.useState(0);
  const [firstCardPainted, setFirstCardPainted] = React.useState(false);

  const [facilities, setFacilities] = React.useState([]); // ✅ must be above facilityByName
  const [mapRef, setMapRef] = React.useState(null);
  const [highlightFacilityId, setHighlightFacilityId] = React.useState(null);
  const [mapFocusTarget, setMapFocusTarget] = React.useState(null);
  const [locationHint, setLocationHint] = React.useState("");
  const [pendingLocate, setPendingLocate] = React.useState(false);
  const [incomingFacilityFocus, setIncomingFacilityFocus] = React.useState(null);
  const appliedFacilityFocusKeyRef = React.useRef("");

  const openDoctor = React.useCallback(
    (doc) => {
      if (doc?.isMockPreview) return;
      const requestedMode = filters.data?.includes("evisit") ? "evisit" : "inperson";
      const requestedFacility =
        incomingFacilityFocus && (incomingFacilityFocus?.id || incomingFacilityFocus?.name)
          ? {
              id: incomingFacilityFocus.id || null,
              name: incomingFacilityFocus.name || "",
              address: incomingFacilityFocus.address || "",
              city: incomingFacilityFocus.city || "",
              country: incomingFacilityFocus.country || "",
              lat: incomingFacilityFocus.lat ?? null,
              lng: incomingFacilityFocus.lng ?? null,
            }
          : doc?.facility || null;

      try {
        // ✅ refresh-safe: keep "last doctor" and also per-id cache
        sessionStorage.setItem("afrimed:lastDoctor", JSON.stringify(doc));
        sessionStorage.setItem(`afrimed:doctor:${doc.id}`, JSON.stringify(doc));
      } catch {
        // ignore
      }

      nav(`/appointments/doctor/${doc.id}`, {
        state: {
          doctor: doc,
          mode: requestedMode,
          facilityId: requestedFacility?.id || null,
          facilityName: requestedFacility?.name || "",
          facility: requestedFacility,
        },
      });
    },
    [filters.data, incomingFacilityFocus, nav]
  );

  const handleCardKeyDown = React.useCallback(
    (e, doc) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      openDoctor(doc);
    },
    [openDoctor]
  );

  React.useEffect(
    () => subscribeDoctorFavorites(() => setFavoriteVersion((value) => value + 1)),
    []
  );

  const userCoords =
    geo?.status === "granted" &&
    geo?.coords?.lat != null &&
    geo?.coords?.lng != null
      ? { lat: Number(geo.coords.lat), lng: Number(geo.coords.lng) }
      : null;

  // ---- derived lookup (safe now) ----
  const facilityByName = React.useMemo(() => {
    const m = new Map();
    for (const f of facilities || []) {
      if (!f?.name) continue;
      m.set(normalizePlace(f.name), f);
    }
    return m;
  }, [facilities]);

  const geocodeFacility = React.useCallback(
    async (query) => {
      const q = String(query || "").trim();
      if (!q || !mapboxToken) return null;

      const cacheKey = q.toLowerCase();
      if (geocodeCacheRef.current.has(cacheKey)) {
        return geocodeCacheRef.current.get(cacheKey);
      }

      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json` +
        `?access_token=${encodeURIComponent(mapboxToken)}&limit=1&types=poi,address,place`;

      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const json = await res.json();
        const center = json?.features?.[0]?.center;
        if (!Array.isArray(center) || center.length < 2) return null;
        const coords = { lng: Number(center[0]), lat: Number(center[1]) };
        if (!Number.isFinite(coords.lng) || !Number.isFinite(coords.lat)) return null;
        geocodeCacheRef.current.set(cacheKey, coords);
        return coords;
      } catch {
        return null;
      }
    },
    [mapboxToken]
  );

  const reverseGeocodeFacility = React.useCallback(
    async (lat, lng) => {
      if (!mapboxToken || !isValidLatLng(lat, lng)) return null;

      const cacheKey = `rev:${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;
      if (geocodeCacheRef.current.has(cacheKey)) {
        return geocodeCacheRef.current.get(cacheKey);
      }

      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          String(lng)
        )},${encodeURIComponent(String(lat))}.json` +
        `?access_token=${encodeURIComponent(mapboxToken)}&limit=1&types=address,poi,place,locality`;

      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const json = await res.json();
        const feature = json?.features?.[0] || null;
        if (!feature) return null;
        const placeName = String(feature?.place_name || "").trim();
        const parts = Array.isArray(feature?.context) ? feature.context : [];
        const locality =
          parts.find((item) => String(item?.id || "").startsWith("place."))?.text ||
          parts.find((item) => String(item?.id || "").startsWith("locality."))?.text ||
          feature?.text ||
          "";
        const country =
          parts.find((item) => String(item?.id || "").startsWith("country."))?.text || "";

        const payload = {
          address: placeName,
          city: String(locality || "").trim(),
          country: String(country || "").trim(),
        };
        geocodeCacheRef.current.set(cacheKey, payload);
        return payload;
      } catch {
        return null;
      }
    },
    [mapboxToken]
  );

  const loadDoctorSourceRows = React.useCallback(async () => {
    const [
      { data: doctorProfiles, error: profileError },
      { data: accountRows },
      { data: doctorServices, error: servicesError },
      consultanciesResult,
    ] = await Promise.all([
      supabase
        .from("doctor_profiles")
        .select(
          "user_id,full_name,first_name,last_name,primary_specialty,specialty,specialties,specialization,verified_status,onboarding_completed,public_discovery_excluded,profile_photo_path,profile_image_url,years_of_experience,years_experience,rating,languages_spoken,consultation_fee,consultation_fees,email,phone,practice_mode,evisit_enabled,evisit_availability,payout_meta,register_country",
        )
        .order("updated_at", { ascending: false })
        .limit(1000),
      supabase
        .from("profiles")
        .select("user_id,account_classification,exclude_from_public_discovery")
        .limit(1000),
      supabase
        .from("doctor_services")
        .select("doctor_id,name,price,currency,is_active,sort_order,updated_at")
        .eq("is_active", true)
        .order("doctor_id", { ascending: true })
        .order("sort_order", { ascending: true })
        .order("updated_at", { ascending: false })
        .limit(2000),
      supabase
        .from("doctor_consultancies")
        .select(
          "doctor_id,id,facility_name,facility_address,facility_city,facility_country,insurance_policy,is_primary,updated_at",
        )
        .order("doctor_id", { ascending: true })
        .order("is_primary", { ascending: false })
        .order("updated_at", { ascending: false })
        .limit(2000),
    ]);

    if (profileError) throw profileError;
    if (servicesError) throw servicesError;

    const doctorIds = new Set(
      (doctorProfiles || []).map((row) => String(row?.user_id || "").trim()).filter(Boolean)
    );
    const accountByUserId = new Map(
      (Array.isArray(accountRows) ? accountRows : [])
        .map((row) => [String(row?.user_id || "").trim(), row])
        .filter(([id]) => doctorIds.has(id))
    );

    const filteredServices = (doctorServices || []).filter((row) =>
      doctorIds.has(String(row?.doctor_id || "").trim())
    );
    const primaryServiceByDoctorId = new Map();
    for (const row of filteredServices) {
      const doctorId = String(row?.doctor_id || "").trim();
      if (!doctorId || primaryServiceByDoctorId.has(doctorId)) continue;
      primaryServiceByDoctorId.set(doctorId, row);
    }

    const { data: consultancies, error: consultancyError } =
      Array.isArray(consultanciesResult?.data) && !consultanciesResult?.error
        ? {
            data: consultanciesResult.data.filter((row) =>
              doctorIds.has(String(row?.doctor_id || "").trim())
            ),
            error: null,
          }
        : { data: null, error: consultanciesResult?.error };

    if (consultancyError) {
      const message = String(consultancyError?.message || "").toLowerCase();
      if (!message.includes("facility_lat") && !message.includes("facility_lng")) {
        throw consultancyError;
      }
      const fallback = await supabase
        .from("doctor_consultancies")
        .select(
          "doctor_id,id,facility_name,facility_address,facility_city,facility_country,insurance_policy,is_primary,updated_at",
        )
        .order("doctor_id", { ascending: true })
        .order("is_primary", { ascending: false })
        .limit(500);
      if (fallback.error) throw fallback.error;
      const filteredConsultancies = (fallback.data || []).filter((row) =>
        doctorIds.has(String(row?.doctor_id || "").trim())
      );
      return mergeLoadedDoctorRows(
        [],
        (doctorProfiles || []).map((profile) => {
          const consultancy = filteredConsultancies.find(
            (row) => String(row?.doctor_id || "").trim() === String(profile?.user_id || "").trim(),
          );
          const primaryService =
            primaryServiceByDoctorId.get(String(profile?.user_id || "").trim()) || null;
          const account = accountByUserId.get(String(profile?.user_id || "").trim()) || null;
          return {
            doctor_user_id: profile.user_id,
            full_name: profile?.full_name || null,
            first_name: profile?.first_name || null,
            last_name: profile?.last_name || null,
            display_name: profile?.full_name || null,
            specialty: profile?.primary_specialty || profile?.specialty || null,
            primary_specialty: profile?.primary_specialty || null,
            specialties: profile?.specialties || null,
            specialization: profile?.specialization || null,
            verified_status: profile?.verified_status || "pending",
            onboarding_completed: profile?.onboarding_completed ?? false,
            account_classification: account?.account_classification || null,
            exclude_from_public_discovery:
              account?.exclude_from_public_discovery ?? false,
            public_discovery_excluded: profile?.public_discovery_excluded ?? false,
            email: profile?.email || null,
            phone: profile?.phone || null,
            practice_mode: profile?.practice_mode || null,
            payout_meta: profile?.payout_meta || null,
            register_country: profile?.register_country || null,
            operating_country: resolveCardOperatingCountry(profile),
            country_inference_source: resolveCardCountryInference(profile).source || null,
            evisit_enabled: profile?.evisit_enabled ?? false,
            evisit_availability: profile?.evisit_availability ?? null,
            profile_photo_path: profile?.profile_photo_path || null,
            profile_image_url: profile?.profile_image_url || null,
            years_of_experience: profile?.years_of_experience ?? profile?.years_experience ?? null,
            languages_spoken: profile?.languages_spoken || null,
            consultation_fee: profile?.consultation_fee ?? null,
            consultation_fees: profile?.consultation_fees || null,
            service_label: primaryService?.name || "",
            service_price: primaryService?.price ?? null,
            service_currency: primaryService?.currency || null,
            consultancy_id: consultancy?.id || null,
            facility_name: consultancy?.facility_name || null,
            facility_address: consultancy?.facility_address || null,
            facility_city: consultancy?.facility_city || null,
            facility_country: consultancy?.facility_country || null,
            insurance_policy: consultancy?.insurance_policy || null,
            display_location:
              consultancy?.facility_city ||
              consultancy?.facility_country ||
              resolveCardOperatingCountry(profile) ||
              consultancy?.facility_name ||
              null,
          };
        }),
      );
    }

    const primaryConsultancyByDoctorId = new Map();
    for (const row of Array.isArray(consultancies) ? consultancies : []) {
      const doctorId = String(row?.doctor_id || "").trim();
      if (!doctorId || primaryConsultancyByDoctorId.has(doctorId)) continue;
      primaryConsultancyByDoctorId.set(doctorId, row);
    }

    return (doctorProfiles || []).map((profile) => {
      const consultancy = primaryConsultancyByDoctorId.get(String(profile?.user_id || "").trim()) || null;
      const primaryService =
        primaryServiceByDoctorId.get(String(profile?.user_id || "").trim()) || null;
      const account = accountByUserId.get(String(profile?.user_id || "").trim()) || null;
      return {
        doctor_user_id: profile.user_id,
        full_name: profile?.full_name || null,
        first_name: profile?.first_name || null,
        last_name: profile?.last_name || null,
        display_name: profile?.full_name || null,
        specialty: profile?.primary_specialty || profile?.specialty || null,
        primary_specialty: profile?.primary_specialty || null,
        specialties: profile?.specialties || null,
        specialization: profile?.specialization || null,
        verified_status: profile?.verified_status || "pending",
        onboarding_completed: profile?.onboarding_completed ?? false,
        account_classification: account?.account_classification || null,
        exclude_from_public_discovery:
          account?.exclude_from_public_discovery ?? false,
        public_discovery_excluded: profile?.public_discovery_excluded ?? false,
        email: profile?.email || null,
        phone: profile?.phone || null,
        practice_mode: profile?.practice_mode || null,
        payout_meta: profile?.payout_meta || null,
        register_country: profile?.register_country || null,
        operating_country: resolveCardOperatingCountry({
          ...profile,
          facility_country: consultancy?.facility_country || null,
        }),
        country_inference_source:
          resolveCardCountryInference({
            ...profile,
            facility_country: consultancy?.facility_country || null,
          }).source || null,
        evisit_enabled: profile?.evisit_enabled ?? false,
        evisit_availability: profile?.evisit_availability ?? null,
        profile_photo_path: profile?.profile_photo_path || null,
        profile_image_url: profile?.profile_image_url || null,
        years_of_experience: profile?.years_of_experience ?? profile?.years_experience ?? null,
        languages_spoken: profile?.languages_spoken || null,
        consultation_fee: profile?.consultation_fee ?? null,
        consultation_fees: profile?.consultation_fees || null,
        service_label: primaryService?.name || "",
        service_price: primaryService?.price ?? null,
        service_currency: primaryService?.currency || null,
        consultancy_id: consultancy?.id || null,
        facility_name: consultancy?.facility_name || null,
        facility_address: consultancy?.facility_address || null,
        facility_city: consultancy?.facility_city || null,
        facility_country: consultancy?.facility_country || null,
        facility_lat: consultancy?.facility_lat ?? null,
        facility_lng: consultancy?.facility_lng ?? null,
        insurance_policy: consultancy?.insurance_policy || null,
        display_location:
          consultancy?.facility_city ||
          consultancy?.facility_country ||
          resolveCardOperatingCountry({
            ...profile,
            facility_country: consultancy?.facility_country || null,
          }) ||
          consultancy?.facility_name ||
          null,
      };
    });
  }, []);

  // ---- callbacks that use mapRef/rows/facilityByName come AFTER ----
  const locateDoctor = React.useCallback(
    async (payloadOrDoctorId) => {
      if (!mapRef) return;

      const payload =
        typeof payloadOrDoctorId === "object" && payloadOrDoctorId
          ? payloadOrDoctorId
          : null;
      const doctorId = payload?.doctorId || payload?.id || payloadOrDoctorId;
      if (!doctorId) return;

      const row = (rows || []).find((r) => r?.doctor_user_id === doctorId);
      if (!row) return;

      const rowAddress = String(
        payload?.address || row?.facility_address || row?.address || "",
      ).trim();
      const rowCity = String(row?.facility_city || "").trim();
      const rowCountry = String(row?.facility_country || "").trim();
      const operatingCountry = resolveCardOperatingCountry(row);
      const operatingCountryCoords = countryCentroid(operatingCountry);
      const practiceMode = String(row?.practice_mode || "").trim().toLowerCase();
      const hasPhysicalLocationText = [row?.facility_name, row?.facility_address, row?.facility_city, row?.facility_country]
        .some((value) => hasDisplayText(value));
      const rowAddressFull = formatFacilityLocation({
        address: rowAddress,
        city: rowCity,
        country: rowCountry,
      });
      const payloadCoords = normalizeFacilityCoords(payload?.lat, payload?.lng);
      const rowCoords = normalizeFacilityCoords(
        row?.facility_lat ?? row?.facility_latitude ?? row?.lat ?? row?.latitude,
        row?.facility_lng ??
          row?.facility_longitude ??
          row?.lng ??
          row?.lon ??
          row?.longitude,
      );

      const rowName = String(
        payload?.name || row?.facility_name || row?.display_location || "",
      ).trim();
      // 1) Use explicit facility coordinates first.
      if (isDoctorMapTargetLatLng(payloadCoords.lat, payloadCoords.lng)) {
        setMapFocusTarget({
          id: payload?.facilityId || row?.consultancy_id || null,
          name: rowName || "Healthcare facility",
          address: rowAddress || "",
          city: rowCity || "",
          country: rowCountry || operatingCountry || "",
          lat: Number(payloadCoords.lat),
          lng: Number(payloadCoords.lng),
        });
        mapRef.flyTo({
          center: [Number(payloadCoords.lng), Number(payloadCoords.lat)],
          zoom: 15,
          duration: 900,
        });
        setLocationHint("");
        return;
      }

      if (isDoctorMapTargetLatLng(rowCoords.lat, rowCoords.lng)) {
        setMapFocusTarget({
          id: row?.consultancy_id || null,
          name: rowName || "Healthcare facility",
          address: rowAddress || "",
          city: rowCity || "",
          country: rowCountry || operatingCountry || "",
          lat: Number(rowCoords.lat),
          lng: Number(rowCoords.lng),
        });
        mapRef.flyTo({
          center: [Number(rowCoords.lng), Number(rowCoords.lat)],
          zoom: 15,
          duration: 900,
        });
        setLocationHint("");
        return;
      }

      // 2) Geocode the saved address string before any dataset lookup.
      const geocodeQuery = rowAddressFull;
      if (geocodeQuery) {
        const geocoded = await geocodeFacility(geocodeQuery);
        if (isDoctorMapTargetLatLng(geocoded?.lat, geocoded?.lng)) {
          setMapFocusTarget({
            id: row?.consultancy_id || null,
            name: rowName || "Healthcare facility",
            address: rowAddress || "",
            city: rowCity || "",
            country: rowCountry || "",
            lat: Number(geocoded.lat),
            lng: Number(geocoded.lng),
          });
          mapRef.flyTo({
            center: [Number(geocoded.lng), Number(geocoded.lat)],
            zoom: 15,
            duration: 900,
          });
          setLocationHint("");
          return;
        }
      }

      if (isDoctorMapTargetLatLng(operatingCountryCoords?.lat, operatingCountryCoords?.lng)) {
        const countryLabel = titleCase(operatingCountry);
        setMapFocusTarget({
          id: doctorId,
          name: row?.display_name || row?.full_name || rowName || "Clinician",
          address: countryLabel,
          city: "",
          country: countryLabel,
          lat: Number(operatingCountryCoords.lat),
          lng: Number(operatingCountryCoords.lng),
        });
        mapRef.flyTo({
          center: [Number(operatingCountryCoords.lng), Number(operatingCountryCoords.lat)],
          zoom: 5.6,
          duration: 900,
        });
        setLocationHint("");
        return;
      }

      if (practiceMode === "virtual_only" || !hasPhysicalLocationText) {
        setLocationHint(
          t(
            "appointments.discovery.countryLocationUnavailable",
            "This clinician has no precise practice pin yet."
          )
        );
        return;
      }

      // 3) Fall back to the cached facility dataset.
      const rowNameKey = normalizePlace(rowName);
      let f = rowNameKey ? facilityByName.get(rowNameKey) : null;

      if (!f && rowNameKey) {
        for (const [k, value] of facilityByName.entries()) {
          if (k.includes(rowNameKey) || rowNameKey.includes(k)) {
            f = value;
            break;
          }
        }
      }

      if (!f && Array.isArray(facilities) && facilities.length > 0) {
        const focusSeed = {
          id: payload?.facilityId || row?.consultancy_id || null,
          name: rowName || "",
          address: rowAddress || "",
          city: rowCity || "",
          country: rowCountry || "",
        };
        const bestFacilityMatch = facilities
          .map((candidate) => ({
            candidate,
            score: scoreFacilityFocusMatch(focusSeed, candidate),
          }))
          .filter(
            (entry) =>
              entry.score > 0 &&
              isDoctorMapTargetLatLng(entry?.candidate?.lat, entry?.candidate?.lng)
          )
          .sort(
            (a, b) =>
              b.score - a.score ||
              String(a?.candidate?.name || "").localeCompare(
                String(b?.candidate?.name || "")
              )
          )[0];
        if (bestFacilityMatch?.candidate) {
          f = bestFacilityMatch.candidate;
        }
      }

      if (isDoctorMapTargetLatLng(f?.lat, f?.lng)) {
        setMapFocusTarget({
          id: f?.id || row?.consultancy_id || null,
          name: f?.name || rowName || "Healthcare facility",
          address: f?.address || rowAddress || "",
          city: f?.city || rowCity || "",
          country: f?.country || rowCountry || "",
          lat: Number(f.lat),
          lng: Number(f.lng),
        });
        setHighlightFacilityId(f?.id || null);
        window.setTimeout(() => setHighlightFacilityId(null), 1200);
        mapRef.flyTo({
          center: [Number(f.lng), Number(f.lat)],
          zoom: 15,
          duration: 900,
        });
        setLocationHint("");
        return;
      }

      // 4) Only now show the truthful unavailable state.
      setLocationHint(
        t(
          "appointments.discovery.facilityLocationUnavailable",
          "Facility location is not available yet for this doctor."
        )
      );
    },
    [facilities, facilityByName, geocodeFacility, mapRef, rows, t]
  );

  const centerMapToUser = React.useCallback(() => {
    if (!mapRef) return;

    if (userCoords) {
      mapRef.flyTo({
        center: [Number(userCoords.lng), Number(userCoords.lat)],
        zoom: 13.5,
        duration: 900,
      });
      setLocationHint("");
      return;
    }

    if (geo?.needsConsent) {
      setLocationHint(
        t(
          "appointments.discovery.allowLocationHint",
          "Allow location to see providers near you and get live directions from your current position."
        )
      );
      setPendingLocate(true);
      geo.openDialog();
      return;
    }

    if (geo?.status === "denied" || geo?.consent === "denied") {
      setLocationHint(
        t(
          "appointments.discovery.locationDisabledHint",
          "Location is disabled. Enable location in browser settings for nearby providers and live directions."
        )
      );
      return;
    }

    setLocationHint(t("appointments.discovery.findingLocation", "Finding your location..."));
    setPendingLocate(true);
    geo.getOnce();
  }, [geo, mapRef, t, userCoords]);

  React.useEffect(() => {
    if (!pendingLocate || !mapRef || !userCoords) return;
    mapRef.flyTo({
      center: [Number(userCoords.lng), Number(userCoords.lat)],
      zoom: 13.5,
      duration: 900,
    });
    setPendingLocate(false);
    setLocationHint("");
  }, [pendingLocate, mapRef, userCoords]);

  React.useEffect(() => {
    if (geo?.consent === "always" && geo?.status === "granted") {
      geo.startWatch();
      return () => geo.stopWatch();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo?.consent, geo?.status]);

  // responsive fork
  const [isDesktop, setIsDesktop] = React.useState(() => {
    try {
      return window.matchMedia?.("(min-width: 768px)")?.matches ?? false;
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    let mql;
    try {
      mql = window.matchMedia("(min-width: 768px)");
      const onChange = () => setIsDesktop(!!mql.matches);
      onChange();
      mql.addEventListener?.("change", onChange);
      return () => mql.removeEventListener?.("change", onChange);
    } catch {
      // ignore
    }
  }, []);
  const [desktopCanScrollPrev, setDesktopCanScrollPrev] = React.useState(false);
  const [desktopCanScrollNext, setDesktopCanScrollNext] = React.useState(false);
  const seededQueryRef = React.useRef("");
  const seededSpecialtyRef = React.useRef("");

  const location = useLocation();
  const directoryContext = React.useMemo(
    () => resolveDirectoryContext(location.pathname, location.search),
    [location.pathname, location.search]
  );
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const mockDoctorsCount = React.useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const raw = Number(params.get("mockDoctors") || "0");
      if (!Number.isFinite(raw)) return 0;
      return Math.max(0, Math.min(6, Math.floor(raw)));
    } catch {
      return 0;
    }
  }, [location.search]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const specialty = String(params.get("specialty") || "").trim();
    const q = String(params.get("q") || "").trim();
    const categorySearch = categoryToSearch(params.get("category"));
    const facilityName = String(params.get("facilityName") || "").trim();
    const facilityAddress = String(params.get("facilityAddress") || "").trim();
    const facilityId = String(params.get("facilityId") || "").trim();
    const facilityLat = toNum(params.get("facilityLat") || params.get("lat"));
    const facilityLng = toNum(params.get("facilityLng") || params.get("lng"));

    if (specialty) {
      seededSpecialtyRef.current = specialty;
      setFilters((prev) => ({
        ...prev,
        data: Array.isArray(prev.data) ? prev.data.filter((x) => x !== "verified") : [],
        specialty: [specialty],
      }));
    } else {
      seededSpecialtyRef.current = "";
    }

    if (facilityName || isValidLatLng(facilityLat, facilityLng) || facilityId) {
      setIncomingFacilityFocus({
        id: facilityId || null,
        name: facilityName || null,
        address: facilityAddress || null,
        lat: facilityLat,
        lng: facilityLng,
      });
    } else {
      setIncomingFacilityFocus(null);
      setMapFocusTarget(null);
      setHighlightFacilityId(null);
      setLocationHint("");
      appliedFacilityFocusKeyRef.current = "";
    }

    if (q) {
      const seededKey = directoryKeyFromSearchValue(q);
      const nextSeed =
        seededKey !== "doctors" && seededKey === directoryContext.key
          ? directoryContext.searchSeed
          : q;
      seededQueryRef.current = nextSeed;
      setQuery(nextSeed);
      return;
    }

    if (categorySearch) {
      seededQueryRef.current = directoryContext.searchSeed;
      setQuery(directoryContext.searchSeed);
      return;
    }

    seededQueryRef.current = "";
    setQuery("");
  }, [directoryContext.key, directoryContext.searchSeed, location.search, setFilters]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query), 220);
    return () => window.clearTimeout(handle);
  }, [query]);

  const immediateCountrySearchFocus = React.useMemo(
    () => resolveCountrySearchFocus(query),
    [query]
  );
  const debouncedCountrySearchFocus = React.useMemo(
    () => resolveCountrySearchFocus(debouncedQuery),
    [debouncedQuery]
  );

  React.useEffect(() => {
    if (!mapRef || !incomingFacilityFocus) return;

    let cancelled = false;

    async function focusIncomingFacility() {
      const key = [
        incomingFacilityFocus.id || "",
        incomingFacilityFocus.name || "",
        incomingFacilityFocus.address || "",
        incomingFacilityFocus.lat ?? "",
        incomingFacilityFocus.lng ?? "",
      ].join("|");
      if (appliedFacilityFocusKeyRef.current === key) return;

      let focus = null;

      if (isValidLatLng(incomingFacilityFocus.lat, incomingFacilityFocus.lng)) {
        const reverse = await reverseGeocodeFacility(
          Number(incomingFacilityFocus.lat),
          Number(incomingFacilityFocus.lng)
        );
        const address = formatFacilityLocation({
          address: incomingFacilityFocus.address || reverse?.address || "",
          city: reverse?.city || "",
          country: incomingFacilityFocus.country || reverse?.country || "",
        });
        focus = {
          id: incomingFacilityFocus.id || null,
          name: incomingFacilityFocus.name || "Healthcare facility",
          address,
          city: reverse?.city || "",
          country: incomingFacilityFocus.country || reverse?.country || "",
          lat: Number(incomingFacilityFocus.lat),
          lng: Number(incomingFacilityFocus.lng),
        };
      } else if (incomingFacilityFocus.name) {
        const geocodeQuery = [
          incomingFacilityFocus.name,
          incomingFacilityFocus.address,
        ]
          .map((value) => String(value || "").trim())
          .filter(Boolean)
          .join(", ");

        if (geocodeQuery) {
          const geocoded = await geocodeFacility(geocodeQuery);
          if (isValidLatLng(geocoded?.lat, geocoded?.lng)) {
            const reverse = await reverseGeocodeFacility(
              Number(geocoded.lat),
              Number(geocoded.lng)
            );
            const address = formatFacilityLocation({
              address: incomingFacilityFocus.address || reverse?.address || "",
              city: reverse?.city || "",
              country: reverse?.country || "",
            });
            const geocodedFocus = {
              id: incomingFacilityFocus.id || null,
              name: incomingFacilityFocus.name || "Healthcare facility",
              address,
              city: reverse?.city || "",
              country: reverse?.country || "",
              lat: Number(geocoded.lat),
              lng: Number(geocoded.lng),
            };
            const focusScore = scoreFacilityFocusMatch(incomingFacilityFocus, geocodedFocus);
            if (focusScore > 0 || !String(incomingFacilityFocus.address || "").trim()) {
              focus = geocodedFocus;
            }
          }
        }

        if (!focus) {
          const normalized = normalizePlace(incomingFacilityFocus.name);
          const direct = facilityByName.get(normalized);
          if (isPreferredMapLatLng(direct?.lat, direct?.lng)) {
            focus = direct;
          } else {
            for (const [k, value] of facilityByName.entries()) {
              if (k.includes(normalized) || normalized.includes(k)) {
                if (isPreferredMapLatLng(value?.lat, value?.lng)) {
                  focus = value;
                  break;
                }
              }
            }
          }
        }
      }

      if (cancelled || !isValidLatLng(focus?.lat, focus?.lng)) return;

      if (focus?.id) {
        setHighlightFacilityId(focus.id);
        window.setTimeout(() => setHighlightFacilityId(null), 1200);
      }
      setMapFocusTarget({
        id: focus?.id || incomingFacilityFocus?.id || null,
        name: focus?.name || incomingFacilityFocus?.name || "Healthcare facility",
        address: focus?.address || "",
        city: focus?.city || "",
        country: focus?.country || "",
        lat: Number(focus.lat),
        lng: Number(focus.lng),
      });
      mapRef.flyTo({
        center: [Number(focus.lng), Number(focus.lat)],
        zoom: 15,
        duration: 900,
      });
      setLocationHint("");
      appliedFacilityFocusKeyRef.current = key;
    }

    focusIncomingFacility();

    return () => {
      cancelled = true;
    };
  }, [mapRef, incomingFacilityFocus, facilityByName, geocodeFacility, reverseGeocodeFacility]);

  const activeFilterCount =
    (filters.data?.length || 0) +
    (filters.specialty?.length || 0) +
    (filters.payment?.length || 0) +
    (filters.date ? 1 : 0);
  const patientProfileCenter = React.useMemo(
    () => resolvePatientProfileMapCenter(profile || {}),
    [profile]
  );

  React.useEffect(() => {
    let alive = true;

    async function load() {
      if (!BYPASS && loading) return;
      if (!BYPASS && !user) return;

      setLoadingList(true);
      setError("");

      try {
        const normalizedFacilityQuery = normalizePlace(incomingFacilityFocus?.name || "");
        const normalizedQuery = normalizePlace(query);
        const seededQuery = String(seededQueryRef.current || "").trim();
        const seededSpecialty = String(seededSpecialtyRef.current || "").trim();
        const shouldSuppressFacilitySeedSearch =
          !!incomingFacilityFocus &&
          (!!normalizedFacilityQuery &&
            (normalizedQuery === normalizedFacilityQuery ||
              normalizedFacilityQuery.includes(normalizedQuery) ||
              normalizedQuery.includes(normalizedFacilityQuery)));
        const effectiveSearch =
          immediateCountrySearchFocus
            ? ""
            : seededQuery && String(query || "").trim() === seededQuery
            ? ""
            : String(query || "")
                .trim()
                .replace(/^["']+|["']+$/g, "");
        const effectiveSpecialty =
          seededSpecialty &&
          Array.isArray(filters.specialty) &&
          filters.specialty.length === 1 &&
          String(filters.specialty[0] || "").trim() === seededSpecialty
            ? []
            : filters.specialty;

        const startedAt = performance.now();
        let primaryCommitted = false;
        const data = await fetchDoctorsDirectoryBrowse({
          search: effectiveSearch,
          verifiedOnly: filters.data?.includes("verified"),
          specialty: effectiveSpecialty, // ✅ array or string
          limit: 1000,
          onPrimaryRows: (primaryRows, trace) => {
            // Date-filtered results require authoritative availability. The
            // normal discovery route can paint canonical cards immediately.
            if (!alive || filters.date || !primaryRows.length) return;
            primaryCommitted = true;
            setRows(primaryRows);
            setLoadingList(false);
            recordStagingPerformance("doctor_directory_primary_committed", {
              durationMs: Math.round(performance.now() - startedAt),
              routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
              primaryDurationMs: trace?.durationMs ?? null,
              primaryRequestCount: trace?.primaryRequestCount ?? 1,
              source: trace?.source || "canonical_browse",
              cardCount: primaryRows.length,
            });
          },
        });
        let list = (Array.isArray(data) ? data : [])
          .filter(isReadyForPatientDiscovery);

        if (!alive) return;

// ✅ language filter
if (filters.language?.length) {
  list = list.filter((r) => {
    const langs = Array.isArray(r.languages_spoken) ? r.languages_spoken : [];
    const hay = langs.map((x) => String(x).toLowerCase());
    return filters.language.some((pick) => hay.includes(String(pick).toLowerCase()));
  });
}

// ✅ virtual quick pill (uses clinician readiness signals, not rendered service text)
if (filters.data?.includes("evisit")) {
  list = list.filter(isVirtualReadyDoctor);
}

// ✅ date: filter against generated real doctor slots
if (filters.date) {
  const availabilityChecks = await Promise.all(
    list.map(async (row) => {
      try {
        const result = await getDoctorBookingAvailability({
          doctorId: row?.doctor_user_id,
          dateISO: filters.date,
        });
        return {
          row,
          available: (result?.slots || []).some((slot) => slot.available),
        };
      } catch {
        return {
          row,
          available: false,
        };
      }
    })
  );

  list = availabilityChecks.filter((item) => item.available).map((item) => item.row);
}

        // ✅ guarantee at least 3 rows (demo padding) — only when explicitly enabled
if (ENABLE_DEMO_PADDING && list.length < 3) {
  const need = 3 - list.length;
  const ids = pickRandomPadIds(need);
  for (const id of ids) list = [...list, makeDemoRow(id)];
}

        setRows(list);
        recordStagingPerformance("doctor_directory_enrichment_complete", {
          durationMs: Math.round(performance.now() - startedAt),
          routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
          firstCardsCommitted: primaryCommitted,
          cardCount: list.length,
          duplicatePageRequestsRemoved: 4,
          schemaRetryRequestsRemoved: 1,
        });
      } catch (e) {
        if (!alive) return;
        setError(e?.message || `Failed to load ${directoryContext.plural.toLowerCase()}`);
        setRows([]);
      } finally {
        if (!alive) return;
        setLoadingList(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [BYPASS, directoryContext.plural, filters, immediateCountrySearchFocus, incomingFacilityFocus, loading, query, user]);

  const cards = React.useMemo(() => {
    const normalizedFacilityQuery = normalizePlace(incomingFacilityFocus?.name || "");
    const normalizedQuery = normalizePlace(debouncedQuery);
    const seededQuery = String(seededQueryRef.current || "").trim();
    const shouldSuppressFacilitySeedSearch =
      !!incomingFacilityFocus &&
      !!normalizedFacilityQuery &&
      (normalizedQuery === normalizedFacilityQuery ||
        normalizedFacilityQuery.includes(normalizedQuery) ||
        normalizedQuery.includes(normalizedFacilityQuery));
    const effectiveSearch =
      seededQuery && String(debouncedQuery || "").trim() === seededQuery
        ? ""
        : String(debouncedQuery || "")
            .trim()
            .replace(/^["']+|["']+$/g, "");

    const list = (Array.isArray(rows) ? rows : []).filter((row) => {
      if (debouncedCountrySearchFocus?.country) {
        return matchesCountrySearchFocus(row, debouncedCountrySearchFocus);
      }
      const localSearch = shouldSuppressFacilitySeedSearch ? "" : effectiveSearch;
      return localSearch ? matchesDirectorySearch(row, localSearch) : true;
    });
    const userId = String(
      user?.id || profile?.user_id || profile?.id || "",
    ).trim();

    let localConsultancy = null;

    if (typeof window !== "undefined" && userId) {
      const consultancies = readDoctorConsultancies(userId);
      if (Array.isArray(consultancies) && consultancies.length > 0) {
        localConsultancy = consultancies[0];
      }
    }

    const mappedCards = list.map((row) => {
      const doctorId = String(row?.doctor_user_id || row?.user_id || "").trim();
      if (!userId || doctorId !== userId) {
        const card = localizeDoctorCard(toCardModel(row), languageCode);
        return {
          ...card,
          isFavorite: isFavoriteDoctor(buildFavoriteDoctorIds(card)),
        };
      }

      const profileFees = parseFeesObject(profile?.consultation_fees);
      const ownServicePrice = resolveMeaningfulPrice(
        row?.service_price,
        profile?.consultation_fee,
        pickFeeFromFeesObject(profileFees)
      );
      const ownCurrency =
        String(
          row?.service_currency ||
            row?.currency ||
            pickCurrencyFromFeesObject(profileFees) ||
            "",
        ).trim() || null;

      const profilePatch = profile && typeof profile === "object"
        ? {
            full_name: profile?.full_name || row?.full_name || null,
            display_name: profile?.full_name || row?.display_name || null,
            specialty: profile?.specialty || profile?.primary_specialty || row?.specialty || null,
            primary_specialty: profile?.primary_specialty || row?.primary_specialty || null,
            specialties: profile?.specialties || row?.specialties || null,
            specialization: profile?.specialization || row?.specialization || null,
            years_of_experience:
              profile?.years_of_experience ??
              profile?.years_experience ??
              row?.years_of_experience ??
              null,
            years_experience:
              profile?.years_experience ??
              profile?.years_of_experience ??
              row?.years_experience ??
              null,
            languages_spoken: profile?.languages_spoken || row?.languages_spoken || null,
            consultation_fee: profile?.consultation_fee ?? row?.consultation_fee ?? null,
            consultation_fees: profile?.consultation_fees || row?.consultation_fees || null,
            profile_image_url: profile?.profile_image_url || row?.profile_image_url || null,
            profile_photo_path: profile?.profile_photo_path || row?.profile_photo_path || null,
          }
        : {};

      const consultPatch = localConsultancy
        ? {
            facility_name:
              row?.facility_name ||
              localConsultancy?.facility_name ||
              localConsultancy?.name ||
              null,
            facility_address:
              row?.facility_address ||
              localConsultancy?.facility_address ||
              localConsultancy?.address ||
              localConsultancy?.address_line ||
              null,
            facility_city:
              row?.facility_city || localConsultancy?.facility_city || localConsultancy?.city || null,
            facility_country:
              row?.facility_country ||
              localConsultancy?.facility_country ||
              localConsultancy?.country ||
              null,
            display_location:
              row?.display_location ||
              localConsultancy?.facility_city ||
              localConsultancy?.city ||
              localConsultancy?.facility_name ||
              localConsultancy?.name ||
              null,
            insurance_policy:
              row?.insurance_policy ||
              localConsultancy?.insurance_policy ||
              localConsultancy?.insurancePolicy ||
              null,
          }
        : {};

      const servicePatch = {
        service_label:
          String(row?.service_label || "").trim() ||
          (ownServicePrice != null ? "Consultation" : ""),
        service_price: ownServicePrice ?? null,
        service_currency: ownCurrency,
        currency: ownCurrency,
      };

      const card = localizeDoctorCard(toCardModel({
        ...row,
        ...profilePatch,
        ...consultPatch,
        ...servicePatch,
      }), languageCode);
      return {
        ...card,
        isFavorite: isFavoriteDoctor(buildFavoriteDoctorIds(card)),
      };
    });

    const facilityScopedCards = incomingFacilityFocus
      ? mappedCards.filter((card) => matchesIncomingFacility(card, incomingFacilityFocus))
      : mappedCards;

    const focusCoords = isValidLatLng(incomingFacilityFocus?.lat, incomingFacilityFocus?.lng)
      ? {
          lat: Number(incomingFacilityFocus.lat),
          lng: Number(incomingFacilityFocus.lng),
        }
      : isValidLatLng(mapFocusTarget?.lat, mapFocusTarget?.lng)
        ? {
            lat: Number(mapFocusTarget.lat),
            lng: Number(mapFocusTarget.lng),
          }
        : null;

    const nearestCards =
      incomingFacilityFocus && facilityScopedCards.length === 0 && focusCoords
        ? mappedCards
            .filter((card) => isValidLatLng(card?.lat, card?.lng))
            .map((card) => ({
              card,
              distanceToFacilityKm: haversineKm(focusCoords, {
                lat: Number(card.lat),
                lng: Number(card.lng),
              }),
            }))
            .sort(
              (a, b) =>
                a.distanceToFacilityKm - b.distanceToFacilityKm ||
                String(a.card?.hospital || "").localeCompare(String(b.card?.hospital || "")) ||
                String(a.card?.name || "").localeCompare(String(b.card?.name || ""))
            )
            .slice(0, 24)
            .map((entry) => entry.card)
        : [];

    const areaMatchedCards =
      incomingFacilityFocus && facilityScopedCards.length === 0
        ? mappedCards
            .map((card) => ({
              card,
              areaScore: scoreFacilityAreaMatch(card, mapFocusTarget || incomingFacilityFocus),
            }))
            .filter((entry) => entry.areaScore > 0)
            .sort(
              (a, b) =>
                b.areaScore - a.areaScore ||
                String(a.card?.hospital || "").localeCompare(String(b.card?.hospital || "")) ||
                String(a.card?.name || "").localeCompare(String(b.card?.name || ""))
            )
            .slice(0, 24)
            .map((entry) => entry.card)
        : [];

    const resolvedCards = incomingFacilityFocus
      ? facilityScopedCards.length > 0
        ? facilityScopedCards
        : nearestCards.length > 0
          ? nearestCards
          : areaMatchedCards
      : mappedCards;

    const sortedCards = userCoords
      ? [...resolvedCards].sort((a, b) => {
          const aHasCoords = isValidLatLng(a?.lat, a?.lng);
          const bHasCoords = isValidLatLng(b?.lat, b?.lng);
          if (aHasCoords && bHasCoords) {
            return (
              haversineKm(userCoords, { lat: Number(a.lat), lng: Number(a.lng) }) -
                haversineKm(userCoords, { lat: Number(b.lat), lng: Number(b.lng) }) ||
              String(a?.hospital || "").localeCompare(String(b?.hospital || "")) ||
              String(a?.name || "").localeCompare(String(b?.name || ""))
            );
          }
          if (aHasCoords) return -1;
          if (bHasCoords) return 1;
          return (
            String(a?.hospital || "").localeCompare(String(b?.hospital || "")) ||
            String(a?.name || "").localeCompare(String(b?.name || ""))
          );
        })
      : resolvedCards;

    if (mockDoctorsCount <= 0) return sortedCards;

    const seed = sortedCards[0] || {
      id: "preview-seed",
      name: "Dr Preview",
      specialty: "General Practice",
      yearsExperience: "12 years",
      rating: "4.7",
      verified: false,
      verificationStatus: "pending",
      hospital: "Preview Clinic",
      address: "Cape Town, South Africa",
      mapAddress: "Cape Town, South Africa",
      serviceLabel: "General consultation",
      serviceLabelPrice: "900.00",
      serviceName: "",
      serviceNamePrice: "",
      currency: "ZAR",
      languages: "English",
      displayLocation: "Cape Town",
      isFavorite: false,
      avatar: "/images/demo/doctors/avatar-fallback.jpg",
      lat: null,
      lng: null,
      facility_id: null,
      facility: null,
    };

    const previewSpecialties = [
      "Dermatologist",
      "Cardiologist",
      "Paediatrician",
      "General Practice",
      "Allergy/Immunology",
      "Endocrinologist",
    ];

    const previewCards = Array.from({ length: mockDoctorsCount }).map((_, i) => {
      const n = i + 1;
      return localizeDoctorCard({
        ...seed,
        id: `mock-preview-${n}`,
        isMockPreview: true,
        name: `Dr Preview ${n}`,
        specialty: previewSpecialties[i % previewSpecialties.length],
        serviceLabel:
          i % 2 === 0 ? "First time consultation" : "Follow-up consultation",
        serviceLabelPrice: i % 2 === 0 ? "1200.00" : "950.00",
        hospital: `Preview Facility ${n}`,
        address: "Cape Town, South Africa",
        mapAddress: "Cape Town, South Africa",
      }, languageCode);
    });

    return [...sortedCards, ...previewCards];
  }, [rows, user?.id, profile, mockDoctorsCount, incomingFacilityFocus, mapFocusTarget, userCoords, debouncedQuery, debouncedCountrySearchFocus, languageCode]);

  const hasDoctorCards = cards.length > 0;

  React.useEffect(() => {
    if (firstCardPaintedRef.current || cards.length === 0) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (firstCardPaintedRef.current) return;
      if (!document.querySelector('[data-afrimed-doctor-card="true"]')) return;
      firstCardPaintedRef.current = true;
      setFirstCardPainted(true);
      recordStagingPerformance("doctor_directory_first_card_painted", {
        routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
        cardCount: cards.length,
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [cards.length]);

  // Load facilities CSV once
  React.useEffect(() => {
    // The 95k-row map facility dataset is optional. Do not fetch or parse it
    // until a DoctorCard has painted (or the canonical directory is empty).
    if (!firstCardPainted && (loadingList || hasDoctorCards)) return undefined;
    let alive = true;

    async function loadFacilities() {
      const startedAt = performance.now();
      try {
        const res = await fetch("/data/health_facilities_sadc.csv", { cache: "no-store" });
        if (!res.ok) return;
        const text = await res.text();

        const lines = text.split(/\r?\n/).filter(Boolean);
        if (lines.length <= 1) return;

        lines.shift(); // header
        const objs = lines
          .map((line, idx) => {
            const cols = line.split(",");
            const country = String(cols[0] || "").trim();
            const city = String(cols[1] || "").trim();
            const name = String(cols[2] || "").trim();
            const lat = toNum(cols[5]);
            const lng = toNum(cols[6]);
        if (!name || !isValidLatLng(lat, lng)) return null;
        return {
              id: `csv-${idx}`,
              name,
              city,
              country,
              address: "",
              lat,
              lng,
            };
          })
          .filter(Boolean);

        if (!alive) return;
        // DoctorMap applies viewport-based marker rendering.
        setFacilities(objs);
        recordStagingPerformance("doctor_directory_facility_enrichment_complete", {
          routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
          durationMs: Math.round(performance.now() - startedAt),
          facilityCount: objs.length,
        });
      } catch {
        // silent
      }
    }

    loadFacilities();
    return () => {
      alive = false;
    };
  }, [firstCardPainted, hasDoctorCards, loadingList]);

  const desktopCards = cards;
  const mapDoctorMarkers = React.useMemo(
    () =>
      (cards || [])
        .filter((card) => isValidLatLng(card?.lat, card?.lng))
        .map((card) => ({
          ...card,
          id: card.id || card.doctor_user_id,
          lat: Number(card.lat),
          lng: Number(card.lng),
        })),
    [cards]
  );
  const mapFacilities = React.useMemo(() => {
    if (!debouncedCountrySearchFocus?.country) return facilities;
    const targetCountry = normalizeCountryText(debouncedCountrySearchFocus.country);
    return (facilities || []).filter(
      (facility) => normalizeCountryText(facility?.country) === targetCountry
    );
  }, [debouncedCountrySearchFocus, facilities]);

  const updateDesktopRailControls = React.useCallback(() => {
    const el = desktopRailRef.current;
    if (!el || !isDesktop) {
      setDesktopCanScrollPrev(false);
      setDesktopCanScrollNext(false);
      return;
    }
    const left = el.scrollLeft;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    setDesktopCanScrollPrev(left > 2);
    setDesktopCanScrollNext(left < maxLeft - 2);
  }, [isDesktop]);

  const scrollDesktopRail = React.useCallback((direction) => {
    const el = desktopRailRef.current;
    if (!el) return;
    const delta = Math.max(360, Math.round(el.clientWidth * 0.55));
    el.scrollBy({
      left: direction === "next" ? delta : -delta,
      behavior: "smooth",
    });
  }, []);

  React.useEffect(() => {
    updateDesktopRailControls();
  }, [updateDesktopRailControls, desktopCards.length, isDesktop]);

  React.useEffect(() => {
    if (!isDesktop) return undefined;
    const onResize = () => updateDesktopRailControls();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isDesktop, updateDesktopRailControls]);

  const mapCenter = React.useMemo(() => {
    const hasActiveVisibleSearchOrFilter =
      !!String(debouncedQuery || "").trim() ||
      activeFilterCount > 0 ||
      !!incomingFacilityFocus;

    if (debouncedCountrySearchFocus) {
      return {
        lat: debouncedCountrySearchFocus.lat,
        lng: debouncedCountrySearchFocus.lng,
        zoom: debouncedCountrySearchFocus.zoom,
      };
    }
    if (isValidLatLng(mapFocusTarget?.lat, mapFocusTarget?.lng)) {
      return { lat: Number(mapFocusTarget.lat), lng: Number(mapFocusTarget.lng), zoom: 5.8 };
    }
    if (userCoords) return { ...userCoords, zoom: 13.5 };
    if (patientProfileCenter) return patientProfileCenter;

    const singleResultCoords =
      hasActiveVisibleSearchOrFilter && cards.length === 1 && isValidLatLng(cards[0]?.lat, cards[0]?.lng)
        ? cards[0]
        : null;
    if (singleResultCoords) {
      return {
        lat: Number(singleResultCoords.lat),
        lng: Number(singleResultCoords.lng),
        zoom: singleResultCoords.mapLocationPrecision === "facility" ? 12 : 5.8,
      };
    }

    return NEUTRAL_AFRICA_CENTER;
  }, [
    activeFilterCount,
    cards,
    debouncedCountrySearchFocus,
    debouncedQuery,
    incomingFacilityFocus,
    mapFocusTarget,
    patientProfileCenter,
    userCoords,
  ]);

  return (
    <div className="fixed inset-0 z-0 h-[100dvh] w-full overflow-hidden">
      {/* FULL MAP FLOOR */}
      <div
        className="absolute inset-0 z-0 h-full w-full"
      >
        <DoctorMap
          diagnosticSurface="doctors-discovery"
          center={mapCenter}
          showUser={!!userCoords}
          user={userCoords}
          doctors={mapDoctorMarkers}
          facilities={mapFacilities}
          highlightFacilityId={highlightFacilityId}
          focusTarget={mapFocusTarget}
          heightClass="h-full"
          frameClassName="w-full h-full"
          mapStyle={getAfriMedMapStyle(isDark)}
          doctorIcon="/markers/afrimed-nearest-doctor.svg"
          facilityIcon="/markers/afrimed-hospital.svg"
          userIcon="/markers/afrimed-user.svg"
          onMapReady={(m) => {
            setMapRef(m);
            recordStagingPerformance("doctor_directory_map_ready", {
              routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
            });
          }}
          onRequireLocation={centerMapToUser}
          onFacilityPopupClose={() => {
            setMapFocusTarget(null);
            appliedFacilityFocusKeyRef.current = "";
          }}
        />
      </div>

      {/* TOP OVERLAYS (search + pills) */}
      <div className="pointer-events-none absolute left-0 right-0 top-0 z-20 md:top-14">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
          <div className="pointer-events-auto">
            <SearchBar
              value={query}
              onChange={setQuery}
              placeholder={directoryContext.searchPlaceholder}
              leftIconSrc={ICONS.back}
              onLeftIconClick={() => nav(-1)}
              rightIconSrc={query?.trim() ? ICONS.cancel : null}
              onRightIconClick={() => setQuery("")}
              variant="solid"
            />
          </div>

          <div className="chips-viewport pointer-events-auto mt-3 no-scrollbar">
            <div className="chips-row">
          {/* Verified */}
          <AfriPill
            label={t("appointments.directory.common.verified", isFrenchLanguageCode(languageCode) ? "Vérifié" : "Verified")}
            iconLeft={ICONS.verified}
            isActive={filters.data?.includes("verified")}
            variant="doctor"
            value="verified"
            onChange={(v) =>
              setFilters((prev) => ({
                ...prev,
                data: toggleInArray(prev.data, v),
              }))
            }
          />

          {/* Nearby */}
          <AfriPill
            label={t("appointments.directory.common.nearby", isFrenchLanguageCode(languageCode) ? "À proximité" : "Nearby")}
            iconLeft={ICONS.nearby}
            isActive={filters.data?.includes("near-you")}
            variant="doctor"
            value="near-you"
            onChange={(v) =>
              setFilters((prev) => ({
                ...prev,
                data: toggleInArray(prev.data, v),
              }))
            }
          />

          {/* Specialty -> opens sheet */}
          <AfriPill
            label={t("appointments.directory.common.specialty", isFrenchLanguageCode(languageCode) ? "Spécialité" : "Specialty")}
            iconLeft={ICONS.specialty}
            isActive={(filters.specialty?.length || 0) > 0}
            variant="doctor"
            value="specialty"
            onChange={openSheet}
          />

          {/* Date -> opens sheet */}
          <AfriPill
            label={t("appointments.directory.common.date", "Date")}
            iconLeft={ICONS.date}
            isActive={!!filters.date}
            variant="doctor"
            value="date"
            onChange={openSheet}
          />

          {/* Language -> opens sheet */}
          <AfriPill
            label={t("appointments.directory.common.language", isFrenchLanguageCode(languageCode) ? "Langue" : "Language")}
            iconLeft={ICONS.language}
            isActive={(filters.language?.length || 0) > 0}
            variant="doctor"
            value="language"
            onChange={openSheet}
          />

          {/* Virtual */}
          <AfriPill
            label={t("appointments.directory.common.virtual", isFrenchLanguageCode(languageCode) ? "Virtuel" : "Virtual")}
            iconLeft={ICONS.evisit}
            isActive={filters.data?.includes("evisit")}
            variant="doctor"
            value="evisit"
            onChange={(v) =>
              setFilters((prev) => ({
                ...prev,
                data: toggleInArray(prev.data, v),
              }))
            }
          />

          {/* Main Filter sheet */}
          <AfriPill
            label={`${t("appointments.directory.common.filter", isFrenchLanguageCode(languageCode) ? "Filtrer" : "Filter")}${activeFilterCount ? ` · ${activeFilterCount}` : ""}`}
            iconLeft={ICONS.filter}
            isActive={activeFilterCount > 0}
            variant="doctor"
            value="main"
            onChange={openSheet}
          />
        </div>
                  </div>
                </div>
              </div>

      {/* RESULTS PANEL */}
      <div
        className="pointer-events-none absolute left-0 right-0 z-30 px-4"
        style={isDesktop ? { bottom: "24px" } : { bottom: "calc(112px + env(safe-area-inset-bottom))" }}
      >
        <div className="w-full max-w-[720px] md:max-w-[1008px] mx-auto">
          <div className="relative">
            {loadingList && (
              <div className="text-body-medium text-on-surface-var px-1 pb-2">
                {t("appointments.discovery.loading", isFrenchLanguageCode(languageCode) ? "Chargement..." : "Loading...")}
              </div>
            )}

            {!loadingList && error && (
              <div className="text-body-medium text-[crimson] px-1 pb-2">{error}</div>
            )}

            {isDesktop ? (
              <>
                <div className="pointer-events-auto mx-auto mb-2 flex max-w-[620px] justify-end gap-2">
                    <button
                      type="button"
                      onClick={centerMapToUser}
                      className="
                        h-9 w-9 rounded-[12px]
                        bg-[rgb(var(--clr-surface-cont-highest))]
                        flex items-center justify-center
                        border border-[rgba(91,107,134,0.20)]
                        shadow-[0_10px_30px_rgba(15,23,42,0.18)]
                      "
                      aria-label="Show my location"
                    >
                      <img
                        src="/icons/doctor/near_me_filled.svg"
                        alt=""
                        className="h-4 w-4"
                        draggable="false"
                      />
                    </button>
                  {desktopCards.length > 1 ? (
                    <>
                    <button
                      type="button"
                      onClick={() => scrollDesktopRail("prev")}
                      disabled={!desktopCanScrollPrev}
                      className={`h-9 w-9 rounded-[12px] flex items-center justify-center border transition ${
                        desktopCanScrollPrev
                          ? "bg-[rgb(var(--clr-surface-cont-highest))] border-[rgba(91,107,134,0.20)] hover:bg-[rgba(91,107,134,0.08)]"
                          : "bg-[rgba(91,107,134,0.06)] border-transparent opacity-60 cursor-not-allowed"
                      }`}
                      aria-label={`Previous ${directoryContext.plural.toLowerCase()}`}
                    >
                      <img src={ICONS.railPrev} alt="" className="h-4 w-4" draggable="false" />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollDesktopRail("next")}
                      disabled={!desktopCanScrollNext}
                      className={`h-9 w-9 rounded-[12px] flex items-center justify-center border transition ${
                        desktopCanScrollNext
                          ? "bg-[rgb(var(--clr-surface-cont-highest))] border-[rgba(91,107,134,0.20)] hover:bg-[rgba(91,107,134,0.08)]"
                          : "bg-[rgba(91,107,134,0.06)] border-transparent opacity-60 cursor-not-allowed"
                      }`}
                      aria-label={`Next ${directoryContext.plural.toLowerCase()}`}
                    >
                      <img src={ICONS.railNext} alt="" className="h-4 w-4" draggable="false" />
                    </button>
                    </>
                  ) : null}
                </div>
                <div
                  ref={desktopRailRef}
                  onScroll={updateDesktopRailControls}
                  className="pointer-events-auto mx-auto flex max-w-[620px] gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 pr-20"
                >
                  {desktopCards.map((doc) => (
                    <div
                      key={doc.id}
                      data-afrimed-doctor-card="true"
                      role="button"
                      tabIndex={0}
                      onClick={() => openDoctor(doc)}
                      onKeyDown={(e) => handleCardKeyDown(e, doc)}
                      className="snap-start shrink-0 w-[520px] max-w-[calc(100vw-128px)] text-left"
                      aria-label={`Open ${doc.name}`}
                    >
                      <DoctorResultCard
                        doctor={doc}
                        onLocate={locateDoctor}
                        onToggleFavorite={() => {}}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="mt-2">
                {!loadingList && !error && (cards?.length || 0) === 0 ? (
                  <Card
                    className="
                      pointer-events-auto
                      p-5
                    "
                  >
                    <div className="text-title-medium text-on-surface mb-1">
                      {incomingFacilityFocus?.name
                        ? t(
                            "appointments.discovery.noFacilityMatches",
                            "No clinicians found for {facilityName}.",
                            { facilityName: incomingFacilityFocus.name },
                          )
                        : debouncedCountrySearchFocus?.label
                          ? `No visible clinicians found in ${debouncedCountrySearchFocus.label}.`
                        : t(
                            "appointments.discovery.noFilterMatches",
                            "No clinicians match your filters.",
                          )}
                    </div>

                    <div className="text-body-large text-on-surface-var mb-4">
                      {incomingFacilityFocus?.name
                        ? t(
                            "appointments.discovery.tryDifferentFacility",
                            "Try a different facility or clear the selected facility to browse the wider directory.",
                          )
                        : debouncedCountrySearchFocus?.label
                          ? "Try another country, clinician name, specialty, service or clear the search to browse visible clinicians."
                        : t(
                            "appointments.discovery.tryClearingFilters",
                            "Try clearing filters or searching a broader term.",
                          )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {/* Clear ALL filters */}
                      <button
                        type="button"
                        onClick={() => {
                          // reset filter chips only, keep search term
                          setFilters((prev) => ({
                            ...prev,
                            data: [],
                            specialty: [],
                            language: [],
                            payment: [],
                            date: null,
                          }));
                        }}
                        className="
                          h-11 px-4 rounded-[12px]
                          bg-[var(--state-primary-12)]
                          text-label-large text-primary
                        "
                      >
                        Clear filters
                      </button>

                      {/* Adjust filters (opens sheet, no navigation) */}
                      <button
                        type="button"
                        onClick={() => openSheet("main")}
                        className="
                          h-11 px-4 rounded-[12px]
                          border border-[rgba(91,107,134,0.20)]
                          text-label-large text-on-surface
                        "
                      >
                        Adjust filters
                      </button>

                      {/* Escape hatch: show unfiltered directory */}
                      <button
                        type="button"
                        onClick={() => {
                          // hard reset to true "show all" state
                          setQuery("");
                          setFilters((prev) => ({
                            ...prev,
                            data: [],
                            specialty: [],
                            language: [],
                            payment: [],
                            date: null,
                          }));
                        }}
                        className="
                          h-11 px-4 rounded-[12px]
                          border border-[rgba(91,107,134,0.20)]
                          text-label-large text-on-surface
                        "
                      >
                        {`Show all ${directoryContext.plural.toLowerCase()}`}
                      </button>
                    </div>
                  </Card>
                ) : (
                <div className="pointer-events-auto -mx-4 pl-4 pr-2">
                  <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 pr-6">
                    {(cards || []).map((doc) => (
                      <div
                        key={doc.id}
                        data-afrimed-doctor-card="true"
                        role="button"
                        tabIndex={0}
                        onClick={() => openDoctor(doc)}
                        onKeyDown={(e) => handleCardKeyDown(e, doc)}
                        className="snap-start shrink-0 w-[388px] max-w-[calc(100vw-56px)] text-left"
                        aria-label={`Open ${doc.name}`}
                      >
                        <DoctorResultCard
                          doctor={doc}
                          onLocate={locateDoctor}
                          onToggleFavorite={() => {}}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                )}
              </div>

                {/* Mobile near-me ABOVE cards */}
                <button
                  type="button"
                  onClick={centerMapToUser}
                  className="
                    pointer-events-auto
                    absolute right-[1.5rem]
                    -top-14
                    h-11 w-11 rounded-[12px]
                    bg-[rgb(var(--clr-surface-cont-highest))]
                    flex items-center justify-center
                    shadow-[0_10px_30px_rgba(15,23,42,0.18)]
                "
                  aria-label="Show my location"
                >
                  <img
                    src="/icons/doctor/near_me_filled.svg"
                    alt=""
                    className="h-5 w-5"
                    draggable="false"
                  />
                </button>
              </>
            )}

            {!loadingList && !error && cards.length === 0 && (
              <div className="text-body-medium text-on-surface-var mt-2">
                {debouncedCountrySearchFocus?.label
                  ? `No visible ${directoryContext.plural.toLowerCase()} found in ${debouncedCountrySearchFocus.label}.`
                  : `No ${directoryContext.plural.toLowerCase()} found.`}
              </div>
            )}
            {locationHint ? (
              <div className="text-body-small text-on-surface-var mt-2">{locationHint}</div>
            ) : null}
            {!locationHint && geo?.error ? (
              <div className="text-body-small text-on-surface-var mt-2">{geo.error}</div>
            ) : null}
          </div>
        </div>
      </div>
      <LocationConsentDialog
        open={geo.dialogOpen}
        onClose={geo.closeDialog}
        onAllowOnce={geo.allowOnce}
        onAllowAlways={geo.allowAlways}
        onDeny={geo.deny}
      />
    </div>
  );
}
